import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../../api/client";
import EmployeeLeaveManagement from "./EmployeeLeaveManagement";
import DocumentPreviewModal from "../../components/DocumentPreviewModal";
import EmployeeTimeline from "../../components/EmployeeTimeline";
import RemoveDocumentButton from "../../components/RemoveDocumentButton";
import EmailDocumentButton from "../../components/EmailDocumentButton";
import { reviewStatusLabel } from "../../utils/reviewStatus";
import { useAuth } from "../../context/AuthContext";
import type {
  AccessLevel,
  AuthMethod,
  BankDetails,
  BenefitPlan,
  Department,
  DisciplinaryCase,
  DriversLicense,
  Education,
  Employee,
  EmployeeAsset,
  EmployeeBenefit,
  EmployeeBonus,
  EmployeeCommission,
  EmployeeDeduction,
  EmployeeSummary,
  EmploymentStatusHistoryEntry,
  EmploymentType,
  EquipmentRequisition,
  Gender,
  JobInformationHistoryEntry,
  MaritalStatus,
  Passkey,
  PayFrequency,
  PaySchedule,
  PayType,
  PreviousWorkExperience,
  RecognitionStats,
  Region,
  RequisitionStatus,
  Site,
  TaxSubRegion,
  VisaRightToWork,
  VisaStatus,
} from "../../types";
import {
  formatPayFrequency,
  formatEmploymentType,
} from "../../utils/formatters";
import DropdownSelect from "../../components/DropdownSelect";
import ComboboxSelect from "../../components/ComboboxSelect";
import EmployeeSearch from "../../components/EmployeeSearch";
import SignatureModal from "../../components/SignatureModal";
import SignatureDetailsModal from "../../components/SignatureDetailsModal";
import { useDropdownOptions } from "../../hooks/useDropdownOptions";
import { useTabParam } from "../../hooks/useTabParam";
import Toast from "../../components/Toast";
import { Banknote, FileSignature, Mail, Smartphone } from "lucide-react";

type Mode = "create" | "edit";

interface CreateResponse {
  employee: Employee;
  initial_pin: string;
}

interface SendWelcomeResult {
  sent: number;
  email_paused?: boolean;
  results: { error: string | null }[];
}

/** One-line email outcome for a PIN reset; never claims a send that did not happen. */
function emailStatusNote(res: { email_sent: boolean; email_paused?: boolean }): string {
  if (res.email_sent) return "Email sent.";
  return res.email_paused ? "Email is paused, so nothing was sent." : "Email not sent.";
}

type FormState = Partial<Employee>;

const INITIAL_FORM: FormState = {
  role: "employee",
  employment_type: "full_time",
  auth_method: "both",
  preferred_language: "en-GB",
};

const REGION_ID_CURRENCY: Record<number, string> = {
  1: "GBP",
  2: "EUR",
  3: "ZAR",
  4: "USD",
};

const EF_TABS = ["profile", "documents", "timeoff", "training", "performance"] as const;

// Payload field whitelist: what we send to the server on create / update.
const PAYLOAD_FIELDS: (keyof Employee)[] = [
  "employee_number",
  "first_name",
  "middle_name",
  "last_name",
  "preferred_name",
  "email",
  "personal_email",
  "phone",
  "work_phone",
  "work_phone_ext",
  "home_phone",
  "date_of_birth",
  "gender",
  "marital_status",
  "site_id",
  "department_id",
  "manager_id",
  "role",
  "employment_type",
  "auth_method",
  "access_level_id",
  "job_title",
  "job_description",
  "division",
  "nin_tax_id",
  "passport_number",
  "contract_hours_weekly",
  "contracted_hours_daily",
  "working_days",
  "start_date",
  "probation_end_date",
  "end_date",
  "address_line_1",
  "address_line_2",
  "city",
  "postcode",
  "country",
  "emergency_contact_name",
  "emergency_contact_phone",
  "emergency_contact_relationship",
  "pay_schedule",
  "pay_type",
  "pay_rate",
  "pay_currency",
  "pay_frequency",
  "annual_salary",
  "hourly_rate",
  "monthly_salary",
  "tax_sub_region",
  "preferred_language",
  "notes",
];

// --- Working days -----------------------------------------------------------

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DEFAULT_WORKING_DAYS = [0, 1, 2, 3, 4];

/** Toggle chips for the days an employee normally works (Monday = 0).
 *  Shown on the employee's home page calendar. Unset means Monday to Friday. */
function WorkingDaysPicker({
  value,
  onChange,
}: {
  value: number[] | null;
  onChange: (days: number[]) => void;
}) {
  const days = value ?? DEFAULT_WORKING_DAYS;
  const toggle = (day: number) => {
    const next = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
    onChange(next.sort((a, b) => a - b));
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Working days">
        {WEEKDAY_LABELS.map((label, day) => {
          const on = days.includes(day);
          return (
            <button
              key={label}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(day)}
              className={[
                "min-h-[36px] min-w-[48px] rounded-full border px-3 text-xs font-semibold transition",
                on
                  ? "border-pme-blue bg-pme-blue text-white"
                  : "border-gray-300 bg-white text-gray-500 hover:border-pme-blue/50",
              ].join(" ")}
            >
              {label}
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-xs text-gray-400">
        Shown on the employee&apos;s home page calendar.
        {value === null ? " Not set yet, so Monday to Friday is assumed." : ""}
      </p>
    </div>
  );
}

function isMaskedValue(v: unknown): boolean {
  if (typeof v !== "string") return false;
  return v.startsWith("***") || v.includes("****");
}

function buildPayload(form: FormState): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of PAYLOAD_FIELDS) {
    const value = form[key];
    if (value === undefined) continue;
    if (isMaskedValue(value)) continue;
    out[key] = value === "" ? null : value;
  }
  return out;
}

// --- Tax calculator types & helpers ----------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TaxCalcConfig = Record<string, any>;

interface TaxTableRow {
  yearly: number;
  monthly: number;
  weekly: number;
  daily: number;
}

interface TaxLineItem {
  label: string;
  row: TaxTableRow;
  style: "deduction" | "employer" | "neutral";
}

interface FullTaxBreakdown {
  gross: TaxTableRow;
  taxableIncome: TaxTableRow;
  lines: TaxLineItem[];
  takeHome: TaxTableRow;
  employerLines: TaxLineItem[];
  totalEmployerCostYearly: number;
}

const REGION_CURRENCY: Record<string, { symbol: string; code: string; locale: string }> = {
  uk: { symbol: "£", code: "GBP", locale: "en-GB" },
  za: { symbol: "R", code: "ZAR", locale: "en-ZA" },
  es: { symbol: "€", code: "EUR", locale: "es-ES" },
  us: { symbol: "$", code: "USD", locale: "en-US" },
};

function getCurrencyInfo(regionCode: string | undefined) {
  return REGION_CURRENCY[regionCode ?? "uk"] ?? REGION_CURRENCY.uk;
}

function fmtMoney(value: number, locale: string, code: string): string {
  return value.toLocaleString(locale, {
    style: "currency",
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function scaleRow(yearly: number): TaxTableRow {
  return {
    yearly,
    monthly: yearly / 12,
    weekly: yearly / 52,
    daily: yearly / 260,
  };
}

function computeAnnualFromRate(
  rate: number,
  frequency: PayFrequency | null | undefined,
  weeklyHours: number,
): number {
  switch (frequency) {
    case "year":
      return rate;
    case "month":
      return rate * 12;
    case "week":
      return rate * 52;
    case "day":
      return rate * 260;
    case "hour":
      return rate * weeklyHours * 52;
    default:
      return rate;
  }
}

function calcBands(gross: number, bands: { from: number; to: number | null; rate: number }[]): number {
  let tax = 0;
  for (const band of bands) {
    if (gross <= band.from) continue;
    const upper = band.to === null ? gross : band.to;
    const inBand = Math.max(0, Math.min(gross, upper) - band.from);
    tax += inBand * band.rate;
  }
  return tax;
}

function calculateFullTax(
  annualGross: number,
  config: TaxCalcConfig,
  regionCode: string,
  subRegionConfig?: TaxCalcConfig | null,
): FullTaxBreakdown {
  const lines: TaxLineItem[] = [];
  const employerLines: TaxLineItem[] = [];
  let totalDeductions = 0;
  let totalEmployerCost = 0;
  let taxable = annualGross;

  if (regionCode === "uk") {
    let pa = config.income_tax?.personal_allowance ?? 0;
    const taper = config.income_tax?.personal_allowance_taper_threshold ?? 100000;
    if (annualGross > taper) {
      pa = Math.max(0, pa - Math.floor((annualGross - taper) / 2));
    }
    taxable = Math.max(0, annualGross - pa);
    const incomeTax = calcBands(annualGross, config.income_tax?.bands ?? []);
    lines.push({ label: "Income tax (PAYE)", row: scaleRow(incomeTax), style: "deduction" });
    totalDeductions += incomeTax;

    const ni = config.employee_ni;
    if (ni) {
      let empNI = 0;
      if (annualGross > ni.primary_threshold) {
        empNI += Math.max(0, Math.min(annualGross, ni.upper_earnings_limit) - ni.primary_threshold) * ni.main_rate;
        if (annualGross > ni.upper_earnings_limit) empNI += (annualGross - ni.upper_earnings_limit) * ni.upper_rate;
      }
      lines.push({ label: "National insurance", row: scaleRow(empNI), style: "deduction" });
      totalDeductions += empNI;
    }
    if (config.employer_ni && annualGross > config.employer_ni.secondary_threshold) {
      const erNI = (annualGross - config.employer_ni.secondary_threshold) * config.employer_ni.rate;
      employerLines.push({ label: "Employer NI", row: scaleRow(erNI), style: "employer" });
      totalEmployerCost += erNI;
    }
  } else if (regionCode === "za") {
    const incomeTax = calcBands(annualGross, config.income_tax?.bands ?? []);
    const rebates = config.income_tax?.rebates;
    const primaryRebate = rebates?.primary ?? 0;
    const netTax = Math.max(0, incomeTax - primaryRebate);
    lines.push({ label: "Income tax (PAYE)", row: scaleRow(netTax), style: "deduction" });
    totalDeductions += netTax;
    taxable = annualGross;

    if (config.uif) {
      const monthlyGross = annualGross / 12;
      const uifMonthly = Math.min(monthlyGross, config.uif.ceiling) * config.uif.employee_rate;
      const uifYearly = uifMonthly * 12;
      lines.push({ label: "UIF (employee)", row: scaleRow(uifYearly), style: "deduction" });
      totalDeductions += uifYearly;
      const erUif = Math.min(monthlyGross, config.uif.ceiling) * config.uif.employer_rate * 12;
      employerLines.push({ label: "UIF (employer)", row: scaleRow(erUif), style: "employer" });
      totalEmployerCost += erUif;
    }
    if (config.sdl && annualGross > (config.sdl.threshold ?? 0)) {
      const sdl = annualGross * config.sdl.rate;
      employerLines.push({ label: "SDL", row: scaleRow(sdl), style: "employer" });
      totalEmployerCost += sdl;
    }
  } else if (regionCode === "es") {
    const irpfBands = config.irpf?.bands ?? [];
    const irpf = calcBands(annualGross, irpfBands);
    lines.push({ label: "IRPF (federal)", row: scaleRow(irpf), style: "deduction" });
    totalDeductions += irpf;
    taxable = annualGross;

    if (subRegionConfig?.irpf?.bands) {
      const regionalIrpf = calcBands(annualGross, subRegionConfig.irpf.bands);
      lines.push({ label: "IRPF (regional)", row: scaleRow(regionalIrpf), style: "deduction" });
      totalDeductions += regionalIrpf;
    }

    if (config.social_security) {
      const empSS = annualGross * config.social_security.employee_rate;
      lines.push({ label: "Social security (employee)", row: scaleRow(empSS), style: "deduction" });
      totalDeductions += empSS;
      const erSS = annualGross * config.social_security.employer_rate;
      employerLines.push({ label: "Social security (employer)", row: scaleRow(erSS), style: "employer" });
      totalEmployerCost += erSS;
    }
  } else if (regionCode === "us") {
    const stdDed = config.federal_tax?.standard_deduction ?? 0;
    taxable = Math.max(0, annualGross - stdDed);
    const fedTax = calcBands(taxable, config.federal_tax?.bands ?? []);
    lines.push({ label: "Federal income tax", row: scaleRow(fedTax), style: "deduction" });
    totalDeductions += fedTax;

    if (config.social_security) {
      const ssWages = Math.min(annualGross, config.social_security.wage_base);
      const ss = ssWages * config.social_security.rate;
      lines.push({ label: "Social security (FICA)", row: scaleRow(ss), style: "deduction" });
      totalDeductions += ss;
      employerLines.push({ label: "Social security (employer)", row: scaleRow(ss), style: "employer" });
      totalEmployerCost += ss;
    }

    if (config.medicare) {
      let mc = annualGross * config.medicare.rate;
      if (config.medicare.additional_rate && annualGross > (config.medicare.additional_threshold ?? 200000)) {
        mc += (annualGross - config.medicare.additional_threshold) * config.medicare.additional_rate;
      }
      lines.push({ label: "Medicare", row: scaleRow(mc), style: "deduction" });
      totalDeductions += mc;
      const erMc = annualGross * config.medicare.rate;
      employerLines.push({ label: "Medicare (employer)", row: scaleRow(erMc), style: "employer" });
      totalEmployerCost += erMc;
    }

    if (subRegionConfig?.state_tax) {
      const st = subRegionConfig.state_tax;
      let stateTax = 0;
      if (st.type === "flat") {
        stateTax = annualGross * st.rate;
      } else if (st.type === "graduated" && st.bands) {
        stateTax = calcBands(annualGross, st.bands);
      }
      if (stateTax > 0) {
        lines.push({ label: `State tax (${subRegionConfig._sub_region ?? ""})`, row: scaleRow(stateTax), style: "deduction" });
        totalDeductions += stateTax;
      }
    }
  } else {
    const bands = config.income_tax?.bands ?? config.irpf?.bands ?? config.federal_tax?.bands ?? [];
    const tax = calcBands(annualGross, bands);
    lines.push({ label: "Income tax", row: scaleRow(tax), style: "deduction" });
    totalDeductions += tax;
  }

  return {
    gross: scaleRow(annualGross),
    taxableIncome: scaleRow(taxable),
    lines,
    takeHome: scaleRow(annualGross - totalDeductions),
    employerLines,
    totalEmployerCostYearly: annualGross + totalEmployerCost,
  };
}

// ---------------------------------------------------------------------------

export default function EmployeeForm() {
  const params = useParams<{ id?: string }>();
  const mode: Mode = params.id ? "edit" : "create";
  const isEdit = mode === "edit";
  const navigate = useNavigate();
  const { employee: currentUser, hasPermission } = useAuth();
  const isSuperAdmin = currentUser?.is_super_admin ?? false;
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useTabParam(EF_TABS, "profile");
  const [directReports, setDirectReports] = useState<EmployeeSummary[]>([]);

  // Compensation visibility — hasPermission handles super_admin override
  const canViewComp =
    hasPermission("can_view_compensation") ||
    hasPermission("can_edit_compensation");
  const canEditComp = hasPermission("can_edit_compensation");
  const canViewBank =
    hasPermission("can_view_bank_details") ||
    hasPermission("can_edit_bank_details");
  const canEditBank = hasPermission("can_edit_bank_details");
  const canViewBonuses =
    hasPermission("can_view_bonuses") ||
    hasPermission("can_edit_bonuses");
  const canEditBonuses = hasPermission("can_edit_bonuses");
  const canManageDeductions = hasPermission("can_manage_deductions");

  const [sites, setSites] = useState<Site[]>([]);
  // Site visibility grants (admin data access scope). Only meaningful when the
  // employee's access level has site_visibility_enabled.
  const [svAllSites, setSvAllSites] = useState(false);
  const [svSiteIds, setSvSiteIds] = useState<number[]>([]);
  const [svSaving, setSvSaving] = useState(false);
  const [svMsg, setSvMsg] = useState("");
  const [departments, setDepartments] = useState<Department[]>([]);
  const [accessLevels, setAccessLevels] = useState<AccessLevel[]>([]);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newPinNotice, setNewPinNotice] = useState<string | null>(null);
  const [createdEmployeeId, setCreatedEmployeeId] = useState<number | null>(
    null,
  );
  const [welcomeSent, setWelcomeSent] = useState(false);
  const [welcomeError, setWelcomeError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  // Bank details state
  const [bankDetails, setBankDetails] = useState<BankDetails | null>(null);
  const [bankForm, setBankForm] = useState<Record<string, string>>({});
  const [bankSaving, setBankSaving] = useState(false);
  const [bankNotice, setBankNotice] = useState<string | null>(null);
  const [bankEditing, setBankEditing] = useState(false);
  const [bankUnmasking, setBankUnmasking] = useState(false);

  // Sensitive fields (NIN/passport) edit mode
  const [sensitiveEditing, setSensitiveEditing] = useState(false);
  const [sensitiveUnmasking, setSensitiveUnmasking] = useState(false);
  const [sensitiveValues, setSensitiveValues] = useState<{ nin_tax_id: string; passport_number: string } | null>(null);

  // History, bonuses, commissions
  const [statusHistory, setStatusHistory] = useState<EmploymentStatusHistoryEntry[]>([]);
  const [jobHistory, setJobHistory] = useState<JobInformationHistoryEntry[]>([]);
  const [bonuses, setBonuses] = useState<EmployeeBonus[]>([]);
  const [commissions, setCommissions] = useState<EmployeeCommission[]>([]);
  const [addingBonus, setAddingBonus] = useState(false);
  const [addingCommission, setAddingCommission] = useState(false);
  const [bonusForm, setBonusForm] = useState({ date: "", amount: "", currency: "GBP", reason: "", comment: "" });
  const [commissionForm, setCommissionForm] = useState({ date: "", amount: "", currency: "GBP", description: "", comment: "" });
  const [bonusSaving, setBonusSaving] = useState(false);
  const [commissionSaving, setCommissionSaving] = useState(false);

  // Deductions state
  const [deductions, setDeductions] = useState<EmployeeDeduction[]>([]);
  const [addingDeduction, setAddingDeduction] = useState(false);
  const [deductionForm, setDeductionForm] = useState<{ name: string; deduction_type: string; amount_type: string; amount: string; currency: string; contribution_type: string; effective_from: string; effective_to: string; notes: string }>({ name: "", deduction_type: "", amount_type: "fixed", amount: "", currency: "GBP", contribution_type: "post_tax", effective_from: "", effective_to: "", notes: "" });
  const [deductionSaving, setDeductionSaving] = useState(false);

  // Benefits state
  const [benefits, setBenefits] = useState<EmployeeBenefit[]>([]);
  const [benefitPlans, setBenefitPlans] = useState<BenefitPlan[]>([]);
  const [addingBenefit, setAddingBenefit] = useState(false);
  const [benefitForm, setBenefitForm] = useState({ benefit_plan_id: "", enrolled_date: "", notes: "" });
  const [benefitSaving, setBenefitSaving] = useState(false);

  // Disciplinary state is managed inside DisciplinarySection component

  // Tax calculator state
  const [taxConfig, setTaxConfig] = useState<TaxCalcConfig | null>(null);
  const [taxConfigLoading, setTaxConfigLoading] = useState(false);
  const [taxYear, setTaxYear] = useState<string>("");
  const [subRegionTaxConfig, setSubRegionTaxConfig] = useState<TaxCalcConfig | null>(null);
  const [availableSubRegions, setAvailableSubRegions] = useState<TaxSubRegion[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);

  // Postcode lookup state
  const [postcodeQuery, setPostcodeQuery] = useState("");
  const [postcodeResults, setPostcodeResults] = useState<
    { line_1: string; line_2: string; town_or_city: string; county: string; postcode?: string; country?: string }[]
  >([]);
  const [postcodeLookupAvailable, setPostcodeLookupAvailable] = useState(false);
  const [postcodeSearching, setPostcodeSearching] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [siteList, regionList, alList] = await Promise.all([
          api.get<Site[]>("/api/sites"),
          api.get<Region[]>("/api/regions"),
          api.get<AccessLevel[]>("/api/access-levels"),
        ]);
        setSites(siteList);
        setRegions(regionList);
        setAccessLevels(alList);
        try {
          const lk = await api.get<{ available: boolean }>("/api/settings/address-lookup-key");
          setPostcodeLookupAvailable(lk.available);
        } catch {
          setPostcodeLookupAvailable(false);
        }
        if (mode === "edit" && params.id) {
          const emp = await api.get<Employee>(`/api/employees/${params.id}`);
          try {
            const sv = await api.get<{ all_sites: boolean; site_ids: number[] }>(
              `/api/employees/${params.id}/site-visibility`);
            setSvAllSites(!!sv?.all_sites);
            setSvSiteIds(sv?.site_ids ?? []);
          } catch { /* non-critical: selector only renders for enabled levels */ }
          setForm(emp);
          if (canViewBank) {
            try {
              const bd = await api.get<BankDetails>(`/api/employees/${params.id}/bank-details`);
              if (bd && "id" in bd) {
                setBankDetails(bd);
              }
            } catch { /* no bank details yet */ }
          }
          try {
            const [sh, jh, bn, cm, dd, bf, bp] = await Promise.all([
              api.get<EmploymentStatusHistoryEntry[]>(`/api/history/employment-status/${params.id}`).catch(() => []),
              api.get<JobInformationHistoryEntry[]>(`/api/history/job-info/${params.id}`).catch(() => []),
              api.get<EmployeeBonus[]>(`/api/employees/${params.id}/bonuses`).catch(() => []),
              api.get<EmployeeCommission[]>(`/api/employees/${params.id}/commissions`).catch(() => []),
              api.get<EmployeeDeduction[]>(`/api/employees/${params.id}/deductions`).catch(() => []),
              api.get<EmployeeBenefit[]>(`/api/employees/${params.id}/benefits`).catch(() => []),
              api.get<BenefitPlan[]>(`/api/benefit-plans`).catch(() => []),
            ]);
            setStatusHistory(sh); setJobHistory(jh); setBonuses(bn); setCommissions(cm);
            setDeductions(dd); setBenefits(bf); setBenefitPlans(bp);
          } catch {}
        } else {
          const defaultSite = siteList[0];
          const defaultRegionId = defaultSite?.region_id;
          setForm((f) => ({
            ...f,
            site_id: defaultSite?.id,
            start_date: new Date().toISOString().slice(0, 10),
            pay_currency: defaultRegionId ? REGION_ID_CURRENCY[defaultRegionId] ?? "GBP" : "GBP",
          }));
        }
      } catch (err) {
        setError((err as Error).message || "Failed to load");
      }
    })();
  }, [mode, params.id]);

  // Departments refetch whenever site changes.
  useEffect(() => {
    const siteId = form.site_id;
    if (!siteId) return;
    void (async () => {
      try {
        const list = await api.get<Department[]>(
          `/api/departments?site_id=${siteId}`,
        );
        setDepartments(list);
      } catch {
        setDepartments([]);
      }
    })();
  }, [form.site_id]);

  // Passkeys for this employee (admin view).
  useEffect(() => {
    if (mode !== "edit" || !params.id) {
      setPasskeys([]);
      return;
    }
    void (async () => {
      try {
        const list = await api.get<Passkey[]>(
          `/api/auth/passkey/admin/${params.id}`,
        );
        setPasskeys(list);
      } catch {
        setPasskeys([]);
      }
    })();
  }, [mode, params.id]);

  const removePasskey = async (passkeyId: number) => {
    if (!confirm("Remove this passkey?")) return;
    await api.delete(`/api/auth/passkey/${passkeyId}`);
    setPasskeys((list) => list.filter((p) => p.id !== passkeyId));
  };

  // Bonus helpers
  const reloadBonuses = async () => {
    if (!params.id) return;
    try {
      const list = await api.get<EmployeeBonus[]>(`/api/employees/${params.id}/bonuses`);
      setBonuses(list);
    } catch { setBonuses([]); }
  };
  const addBonus = async () => {
    if (!params.id || !bonusForm.date || !bonusForm.amount) return;
    setBonusSaving(true);
    try {
      await api.post(`/api/employees/${params.id}/bonuses`, {
        date: bonusForm.date,
        amount: bonusForm.amount,
        currency: bonusForm.currency || "GBP",
        reason: bonusForm.reason || null,
        comment: bonusForm.comment || null,
      });
      setBonusForm({ date: "", amount: "", currency: "GBP", reason: "", comment: "" });
      setAddingBonus(false);
      await reloadBonuses();
    } finally { setBonusSaving(false); }
  };
  const deleteBonus = async (bonusId: number) => {
    if (!params.id || !confirm("Delete this bonus?")) return;
    await api.delete(`/api/employees/${params.id}/bonuses/${bonusId}`);
    await reloadBonuses();
  };

  // Commission helpers
  const reloadCommissions = async () => {
    if (!params.id) return;
    try {
      const list = await api.get<EmployeeCommission[]>(`/api/employees/${params.id}/commissions`);
      setCommissions(list);
    } catch { setCommissions([]); }
  };
  const addCommission = async () => {
    if (!params.id || !commissionForm.date || !commissionForm.amount) return;
    setCommissionSaving(true);
    try {
      await api.post(`/api/employees/${params.id}/commissions`, {
        date: commissionForm.date,
        amount: commissionForm.amount,
        currency: commissionForm.currency || "GBP",
        description: commissionForm.description || null,
        comment: commissionForm.comment || null,
      });
      setCommissionForm({ date: "", amount: "", currency: "GBP", description: "", comment: "" });
      setAddingCommission(false);
      await reloadCommissions();
    } finally { setCommissionSaving(false); }
  };
  const deleteCommission = async (commissionId: number) => {
    if (!params.id || !confirm("Delete this commission?")) return;
    await api.delete(`/api/employees/${params.id}/commissions/${commissionId}`);
    await reloadCommissions();
  };

  // Deduction helpers
  const reloadDeductions = async () => {
    if (!params.id) return;
    try { setDeductions(await api.get<EmployeeDeduction[]>(`/api/employees/${params.id}/deductions`)); } catch { setDeductions([]); }
  };
  const addDeduction = async () => {
    if (!params.id || !deductionForm.name || !deductionForm.amount) return;
    setDeductionSaving(true);
    try {
      await api.post(`/api/employees/${params.id}/deductions`, {
        name: deductionForm.name, deduction_type: deductionForm.deduction_type || "other",
        amount_type: deductionForm.amount_type, amount: parseFloat(deductionForm.amount),
        currency: deductionForm.currency || "GBP", contribution_type: deductionForm.contribution_type,
        effective_from: deductionForm.effective_from || null, effective_to: deductionForm.effective_to || null,
        notes: deductionForm.notes || null,
      });
      setDeductionForm({ name: "", deduction_type: "", amount_type: "fixed", amount: "", currency: "GBP", contribution_type: "post_tax", effective_from: "", effective_to: "", notes: "" });
      setAddingDeduction(false);
      await reloadDeductions();
    } finally { setDeductionSaving(false); }
  };
  const deleteDeduction = async (dedId: number) => {
    if (!params.id || !confirm("Delete this deduction?")) return;
    await api.delete(`/api/employees/${params.id}/deductions/${dedId}`);
    await reloadDeductions();
  };

  // Benefit helpers
  const reloadBenefits = async () => {
    if (!params.id) return;
    try { setBenefits(await api.get<EmployeeBenefit[]>(`/api/employees/${params.id}/benefits`)); } catch { setBenefits([]); }
  };
  const addBenefit = async () => {
    if (!params.id || !benefitForm.benefit_plan_id) return;
    setBenefitSaving(true);
    try {
      await api.post(`/api/employees/${params.id}/benefits`, {
        benefit_plan_id: parseInt(benefitForm.benefit_plan_id),
        enrolled_date: benefitForm.enrolled_date || new Date().toISOString().slice(0, 10),
        notes: benefitForm.notes || null,
      });
      setBenefitForm({ benefit_plan_id: "", enrolled_date: "", notes: "" });
      setAddingBenefit(false);
      await reloadBenefits();
    } finally { setBenefitSaving(false); }
  };
  const deleteBenefit = async (bId: number) => {
    if (!params.id || !confirm("Remove this benefit enrolment?")) return;
    await api.delete(`/api/employees/${params.id}/benefits/${bId}`);
    await reloadBenefits();
  };

  // Direct reports for the sidebar — only in edit mode once we know the id.
  useEffect(() => {
    if (mode !== "edit" || !form.id) {
      setDirectReports([]);
      return;
    }
    void (async () => {
      try {
        const list = await api.get<EmployeeSummary[]>(
          `/api/employees?manager_id=${form.id}&is_active=true`,
        );
        setDirectReports(list);
      } catch {
        setDirectReports([]);
      }
    })();
  }, [mode, form.id]);

  // Fetch tax config when site changes (for tax calculator).
  const selectedSite = useMemo(
    () => sites.find((s) => s.id === form.site_id),
    [sites, form.site_id],
  );

  useEffect(() => {
    if (!selectedSite || !canViewComp) {
      setTaxConfig(null);
      setAvailableSubRegions([]);
      return;
    }
    const regionId = selectedSite.region_id;
    setTaxConfigLoading(true);
    void (async () => {
      try {
        const [res, subRes] = await Promise.all([
          api.get<{
            configured: boolean;
            config_data?: TaxCalcConfig;
            tax_year?: string;
          }>(`/api/settings/tax?region_id=${regionId}`),
          api.get<{ sub_regions: TaxSubRegion[] }>(`/api/settings/tax/sub-regions?region_id=${regionId}`),
        ]);
        if (res.configured && res.config_data) {
          setTaxConfig(res.config_data);
          setTaxYear(res.tax_year ?? "");
        } else {
          setTaxConfig(null);
          setTaxYear("");
        }
        setAvailableSubRegions(subRes.sub_regions ?? []);
      } catch {
        setTaxConfig(null);
        setAvailableSubRegions([]);
      } finally {
        setTaxConfigLoading(false);
      }
    })();
  }, [selectedSite, canViewComp]);

  useEffect(() => {
    if (!selectedSite || !canViewComp || !form.tax_sub_region) {
      setSubRegionTaxConfig(null);
      return;
    }
    const regionId = selectedSite.region_id;
    void (async () => {
      try {
        const res = await api.get<{
          configured: boolean;
          config_data?: TaxCalcConfig;
        }>(`/api/settings/tax?region_id=${regionId}&sub_region=${form.tax_sub_region}`);
        if (res.configured && res.config_data) {
          setSubRegionTaxConfig({ ...res.config_data, _sub_region: form.tax_sub_region });
        } else {
          setSubRegionTaxConfig(null);
        }
      } catch {
        setSubRegionTaxConfig(null);
      }
    })();
  }, [selectedSite, canViewComp, form.tax_sub_region]);

  const selectedRegion = useMemo(
    () => regions.find((r) => r.id === selectedSite?.region_id),
    [regions, selectedSite],
  );
  const currInfo = useMemo(
    () => getCurrencyInfo(selectedRegion?.code),
    [selectedRegion],
  );
  const CURRENCY_SYMBOLS: Record<string, string> = { GBP: "£", EUR: "€", ZAR: "R", USD: "$" };
  const activeCurrency = form.pay_currency ?? currInfo.code;
  const activeSymbol = CURRENCY_SYMBOLS[activeCurrency] ?? currInfo.symbol;
  const activeLocale = Object.entries(REGION_CURRENCY).find(
    ([, v]) => v.code === activeCurrency,
  )?.[1]?.locale ?? currInfo.locale;
  const fmt = (v: number) => fmtMoney(v, activeLocale, activeCurrency);

  const weeklyHrs = parseFloat(form.contract_hours_weekly ?? "") || 37.5;

  const annualSalary = useMemo(() => {
    const rate = parseFloat(form.pay_rate ?? "");
    if (!rate || rate <= 0) return 0;
    return computeAnnualFromRate(rate, form.pay_frequency, weeklyHrs);
  }, [form.pay_rate, form.pay_frequency, weeklyHrs]);

  const taxBreakdown = useMemo<FullTaxBreakdown | null>(() => {
    if (!annualSalary || !taxConfig) return null;
    return calculateFullTax(annualSalary, taxConfig, selectedRegion?.code ?? "uk", subRegionTaxConfig);
  }, [annualSalary, taxConfig, selectedRegion?.code, subRegionTaxConfig]);

  const title = useMemo(
    () =>
      mode === "create"
        ? "New employee"
        : `Edit ${form.first_name ?? ""} ${form.last_name ?? ""}`.trim(),
    [mode, form.first_name, form.last_name],
  );

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = buildPayload(form);
      if (payload.site_id) payload.site_id = Number(payload.site_id);
      if (payload.department_id)
        payload.department_id = Number(payload.department_id);
      if (payload.manager_id)
        payload.manager_id = Number(payload.manager_id);
      if (payload.access_level_id)
        payload.access_level_id = Number(payload.access_level_id);
      if (mode === "create") {
        const result = await api.post<CreateResponse>("/api/employees", {
          ...payload,
          send_welcome_email: false,
        });
        setNewPinNotice(
          `${result.employee.first_name}'s initial PIN is ${result.initial_pin}. ` +
            `Share it securely; they must change it on first login.`,
        );
        setCreatedEmployeeId(result.employee.id);
      } else if (params.id) {
        await api.put(`/api/employees/${params.id}`, payload);
        setToast("Changes saved");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const resetPin = async () => {
    if (!params.id) return;
    if (
      !confirm(
        "Reset this employee's clock PIN? They will need the new one to sign in.",
      )
    ) {
      return;
    }
    const res = await api.post<{ new_pin: string; email_sent: boolean; email_paused?: boolean }>(
      `/api/employees/${params.id}/reset-clock-pin`,
    );
    setNewPinNotice(
      `New PIN: ${res.new_pin}. ${emailStatusNote(res)} Share it securely — this will not be shown again.`,
    );
  };

  const unbindDevice = async () => {
    if (!params.id) return;
    await api.put(`/api/employees/${params.id}/unbind-device`);
    setForm((f) => ({
      ...f,
      device_fingerprint: null,
      device_bound_at: null,
    }));
  };

  const deactivate = async () => {
    if (!params.id) return;
    if (
      !confirm(
        "Deactivate this employee? They will no longer be able to sign in.",
      )
    ) {
      return;
    }
    await api.put(`/api/employees/${params.id}/deactivate`);
    navigate("/admin/employees");
  };

  // -------------------------------------------------------------------------
  // Profile tab content: the existing form + status messages. In edit mode it
  // renders inside the "Profile" tab; in create mode it's the only thing on
  // the page.
  // -------------------------------------------------------------------------
  const profileFormJsx = (
    <>
      {newPinNotice && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-900">
          <p>{newPinNotice}</p>
          {createdEmployeeId && !welcomeSent && (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-lg bg-pme-blue px-4 py-2 text-xs font-semibold text-white"
                onClick={async () => {
                  setWelcomeError(null);
                  try {
                    const res = await api.post<SendWelcomeResult>("/api/employees/send-welcome-emails", {
                      employee_ids: [createdEmployeeId],
                    });
                    if (res.sent > 0) {
                      setWelcomeSent(true);
                    } else if (res.email_paused) {
                      setWelcomeError("Email is paused. No welcome email was sent; the PIN above still works.");
                    } else {
                      setWelcomeError(`Welcome email not sent. ${res.results[0]?.error ?? ""}`.trim());
                    }
                  } catch {
                    setWelcomeError("Welcome email not sent.");
                  }
                }}
              >
                Send Welcome Email Now
              </button>
              <button
                type="button"
                className="rounded-lg border border-gray-300 px-4 py-2 text-xs"
                onClick={() => {
                  setNewPinNotice(null);
                  navigate("/admin/employees");
                }}
              >
                I'll Send Later
              </button>
            </div>
          )}
          {welcomeSent && (
            <p className="mt-2 text-xs text-green-700">Welcome email sent.</p>
          )}
          {welcomeError && !welcomeSent && (
            <p className="mt-2 text-xs text-amber-800">{welcomeError}</p>
          )}
          {(welcomeSent || !createdEmployeeId) && (
            <button
              type="button"
              className="mt-2 text-xs font-semibold text-pme-blue"
              onClick={() => {
                setNewPinNotice(null);
                setCreatedEmployeeId(null);
                setWelcomeSent(false);
                setWelcomeError(null);
                if (mode === "create") navigate("/admin/employees");
              }}
            >
              Done
            </button>
          )}
        </div>
      )}

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <form onSubmit={submit} className="space-y-6">
        {/* ----------------------------------------------------------------- */}
        {/* 1. Personal                                                       */}
        {/* ----------------------------------------------------------------- */}
        <Section title="Personal">
          <Row>
            <Field label="First name">
              <input
                required
                value={form.first_name ?? ""}
                onChange={(e) => update("first_name", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Middle name">
              <input
                value={form.middle_name ?? ""}
                onChange={(e) => update("middle_name", e.target.value)}
                className="input"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Last name">
              <input
                required
                value={form.last_name ?? ""}
                onChange={(e) => update("last_name", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Preferred name">
              <input
                value={form.preferred_name ?? ""}
                onChange={(e) => update("preferred_name", e.target.value)}
                className="input"
                placeholder="Nickname or preferred first name"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Email">
              <input
                required
                type="email"
                value={form.email ?? ""}
                onChange={(e) => update("email", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Personal email">
              <input
                type="email"
                value={form.personal_email ?? ""}
                onChange={(e) => update("personal_email", e.target.value)}
                className="input"
              />
            </Field>
          </Row>
          <Row>
            <Field label={form.date_of_birth ? `Date of birth (Age: ${Math.floor((Date.now() - new Date(form.date_of_birth).getTime()) / 31557600000)})` : "Date of birth"}>
              <input
                type="date"
                value={form.date_of_birth ?? ""}
                onChange={(e) => update("date_of_birth", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Gender">
              <DropdownSelect
                listCode="gender"
                value={form.gender ?? ""}
                onChange={(v) => update("gender", v === "" ? null : (v as Gender))}
                placeholder="-- Not specified --"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Marital status">
              <DropdownSelect
                listCode="marital_status"
                value={form.marital_status ?? ""}
                onChange={(v) => update("marital_status", v === "" ? null : (v as MaritalStatus))}
                placeholder="-- Not specified --"
              />
            </Field>
            <Field label="Preferred language">
              <DropdownSelect
                listCode="language"
                value={form.preferred_language ?? "en-GB"}
                onChange={(v) => update("preferred_language", v)}
              />
            </Field>
          </Row>
        </Section>

        {/* ----------------------------------------------------------------- */}
        {/* 2. Address                                                        */}
        {/* ----------------------------------------------------------------- */}
        <Section title="Address">
          {postcodeLookupAvailable && selectedRegion?.code === "uk" && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm">
                <span className="block text-xs font-medium text-gray-700">Postcode lookup</span>
                <input
                  type="text"
                  value={postcodeQuery}
                  onChange={(e) => setPostcodeQuery(e.target.value.toUpperCase())}
                  placeholder="e.g. SW1A 1AA"
                  className="mt-1 w-36 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <button
                type="button"
                disabled={postcodeSearching || !postcodeQuery.trim()}
                onClick={async () => {
                  setPostcodeSearching(true);
                  setPostcodeResults([]);
                  try {
                    const res = await api.get<{ addresses: typeof postcodeResults }>(
                      `/api/settings/address-lookup/${encodeURIComponent(postcodeQuery.trim())}`,
                    );
                    setPostcodeResults(res.addresses ?? []);
                  } catch {
                    setPostcodeResults([]);
                  } finally {
                    setPostcodeSearching(false);
                  }
                }}
                className="rounded-lg bg-pme-blue px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {postcodeSearching ? "Searching..." : "Find"}
              </button>
              {postcodeResults.length > 0 && (
                <select
                  className="mt-1 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  defaultValue=""
                  onChange={(e) => {
                    const idx = Number(e.target.value);
                    const addr = postcodeResults[idx];
                    if (!addr) return;
                    setForm((f) => ({
                      ...f,
                      address_line_1: addr.line_1 || "",
                      address_line_2: addr.line_2 || "",
                      city: addr.town_or_city || "",
                      postcode: postcodeQuery.trim(),
                      country: "United Kingdom",
                    }));
                    setPostcodeResults([]);
                    setPostcodeQuery("");
                  }}
                >
                  <option value="" disabled>Select an address ({postcodeResults.length} found)</option>
                  {postcodeResults.map((a, i) => (
                    <option key={i} value={i}>
                      {[a.line_1, a.line_2, a.town_or_city].filter(Boolean).join(", ")}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          <Field label="Address line 1">
            <input
              value={form.address_line_1 ?? ""}
              onChange={(e) => update("address_line_1", e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Address line 2">
            <input
              value={form.address_line_2 ?? ""}
              onChange={(e) => update("address_line_2", e.target.value)}
              className="input"
            />
          </Field>
          <Row>
            <Field label="City">
              <input
                value={form.city ?? ""}
                onChange={(e) => update("city", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Postcode">
              <input
                value={form.postcode ?? ""}
                onChange={(e) => update("postcode", e.target.value)}
                className="input"
              />
            </Field>
          </Row>
          <Field label="Country">
            <input
              value={form.country ?? ""}
              onChange={(e) => update("country", e.target.value)}
              className="input max-w-xs"
            />
          </Field>
        </Section>

        {/* ----------------------------------------------------------------- */}
        {/* 3. Contact                                                        */}
        {/* ----------------------------------------------------------------- */}
        <Section title="Contact">
          <Row>
            <Field label="Phone (mobile)">
              <input
                type="tel"
                value={form.phone ?? ""}
                onChange={(e) => update("phone", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Home phone">
              <input
                type="tel"
                value={form.home_phone ?? ""}
                onChange={(e) => update("home_phone", e.target.value)}
                className="input"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Work phone">
              <input
                type="tel"
                value={form.work_phone ?? ""}
                onChange={(e) => update("work_phone", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Extension">
              <input
                value={form.work_phone_ext ?? ""}
                onChange={(e) => update("work_phone_ext", e.target.value)}
                className="input"
                placeholder="e.g. 1234"
              />
            </Field>
          </Row>
        </Section>

        {/* ----------------------------------------------------------------- */}
        {/* 4. Job Information                                                */}
        {/* ----------------------------------------------------------------- */}
        <Section title="Job Information">
          <Row>
            <Field
              label="Employee / Payroll Number"
              hint="Used as the payroll reference for exports. Auto-generated if left blank."
            >
              <input
                value={form.employee_number ?? ""}
                onChange={(e) => update("employee_number", e.target.value)}
                placeholder="PME-0007"
                className="input"
              />
            </Field>
            <Field label="Site">
              <select
                value={form.site_id ?? ""}
                onChange={(e) => {
                  const siteId = Number(e.target.value);
                  update("site_id", siteId);
                  update("department_id", null);
                  const newSite = sites.find((s) => s.id === siteId);
                  if (newSite) {
                    const regionCurrency = REGION_ID_CURRENCY[newSite.region_id] ?? "GBP";
                    update("pay_currency", regionCurrency);
                  }
                }}
                className="input"
              >
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
          </Row>
          <Row>
            <Field label="Department">
              <select
                value={form.department_id ?? ""}
                onChange={(e) =>
                  update(
                    "department_id",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                className="input"
              >
                <option value="">-- General / none --</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}{d.site_name ? ` (${d.site_name})` : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Line manager">
              <EmployeeSearch
                value={form.manager_id ?? null}
                onChange={(v) => update("manager_id", v)}
                placeholder="Search managers..."
                emptyLabel="-- No line manager --"
                excludeIds={params.id ? [Number(params.id)] : []}
              />
            </Field>
          </Row>
          <Row>
            <Field label="Job title">
              <ComboboxSelect
                listCode="job_title"
                value={form.job_title ?? ""}
                onChange={(v) => update("job_title", v)}
                placeholder="e.g. Warehouse Operative, Baker"
              />
            </Field>
            <Field label="Division">
              <input
                value={form.division ?? ""}
                onChange={(e) => update("division", e.target.value)}
                className="input"
                placeholder="e.g. Retail, Production"
              />
            </Field>
          </Row>
          <Row>
            <Field label={selectedRegion?.code === "uk" ? "NI Number" : selectedRegion?.code === "za" ? "ID Number" : selectedRegion?.code === "es" ? "NIE / DNI" : selectedRegion?.code === "us" ? "SSN" : "Tax ID"}>
              {sensitiveEditing ? (
                <input
                  value={sensitiveValues?.nin_tax_id ?? form.nin_tax_id ?? ""}
                  onChange={(e) => {
                    setSensitiveValues((p) => ({ nin_tax_id: e.target.value, passport_number: p?.passport_number ?? form.passport_number ?? "" }));
                    update("nin_tax_id", e.target.value);
                  }}
                  className="input"
                  placeholder={selectedRegion?.code === "uk" ? "e.g. QQ 12 34 56 C" : ""}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-800 font-mono">{form.nin_tax_id || "—"}</p>
                  {form.corrupted_fields?.includes("nin_tax_id") && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200" title="This value may have been corrupted and needs to be re-entered">Needs re-entry</span>
                  )}
                  {mode === "edit" && hasPermission("can_manage_employees") && (
                    <button
                      type="button"
                      disabled={sensitiveUnmasking}
                      onClick={async () => {
                        setSensitiveUnmasking(true);
                        try {
                          const res = await api.get<{ nin_tax_id: string; passport_number: string }>(`/api/employees/${form.id}/sensitive-fields/unmasked`);
                          setSensitiveValues(res);
                          update("nin_tax_id", res.nin_tax_id ?? "");
                          update("passport_number", res.passport_number ?? "");
                          setSensitiveEditing(true);
                        } catch { /* permission denied or error */ }
                        setSensitiveUnmasking(false);
                      }}
                      className="text-pme-blue hover:text-pme-blue/80"
                      title="Edit"
                    >
                      {sensitiveUnmasking ? (
                        <span className="text-xs text-gray-400">...</span>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" /></svg>
                      )}
                    </button>
                  )}
                </div>
              )}
            </Field>
            <Field label="Passport Number">
              {sensitiveEditing ? (
                <div className="flex items-center gap-2">
                  <input
                    value={sensitiveValues?.passport_number ?? form.passport_number ?? ""}
                    onChange={(e) => {
                      setSensitiveValues((p) => ({ nin_tax_id: p?.nin_tax_id ?? form.nin_tax_id ?? "", passport_number: e.target.value }));
                      update("passport_number", e.target.value);
                    }}
                    className="input"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSensitiveEditing(false);
                      setSensitiveValues(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                    title="Done"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" /></svg>
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-800 font-mono">{form.passport_number || "—"}</p>
                  {form.corrupted_fields?.includes("passport_number") && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200" title="This value may have been corrupted and needs to be re-entered">Needs re-entry</span>
                  )}
                </div>
              )}
            </Field>
          </Row>
          <Field label="Job description">
            <textarea
              value={form.job_description ?? ""}
              onChange={(e) => update("job_description", e.target.value)}
              rows={3}
              className="input"
              placeholder="Role description and responsibilities"
            />
          </Field>
          <Row>
            <Field label="Employment type">
              <DropdownSelect
                listCode="employment_type"
                value={form.employment_type ?? "full_time"}
                onChange={(v) => update("employment_type", v as EmploymentType)}
              />
            </Field>
          </Row>
          <Row>
            <Field label="Access level" hint="Controls permissions and admin panel access">
              <select
                value={form.access_level_id ?? ""}
                onChange={(e) =>
                  update(
                    "access_level_id",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                className="input"
              >
                <option value="">-- None --</option>
                {accessLevels.map((al) => (
                  <option key={al.id} value={al.id}>
                    {al.name}
                    {al.is_admin ? " (Admin)" : ""}
                  </option>
                ))}
              </select>
            </Field>
          </Row>
          {(() => {
            const lvl = accessLevels.find((a) => a.id === Number(form.access_level_id));
            // Locked levels never get the selector — they are hard-locked to
            // their own site in the backend regardless of what is stored.
            if (!lvl?.site_visibility_enabled) return null;
            return (
              <Row>
                <Field
                  label="Site visibility"
                  hint="Which sites' employees this person can see in admin areas. Their own record and their reports are always visible."
                >
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={svAllSites}
                        onChange={(e) => { setSvAllSites(e.target.checked); if (e.target.checked) setSvSiteIds([]); }}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <span className="text-gray-700">All sites</span>
                    </label>
                    {!svAllSites && (
                      <div className="flex flex-wrap gap-2">
                        {sites.map((st) => (
                          <label key={st.id} className="flex items-center gap-1.5 rounded border border-gray-200 px-2 py-1 text-xs">
                            <input
                              type="checkbox"
                              checked={svSiteIds.includes(st.id)}
                              onChange={(e) => setSvSiteIds((prev) =>
                                e.target.checked ? [...prev, st.id] : prev.filter((x) => x !== st.id))}
                              className="h-3.5 w-3.5 rounded border-gray-300"
                            />
                            {st.name}
                          </label>
                        ))}
                      </div>
                    )}
                    {!svAllSites && svSiteIds.length === 0 && (
                      <p className="text-xs text-amber-700">
                        No sites selected — this user will see no employees in admin areas
                        apart from their own record and their direct/indirect reports.
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={svSaving || !isEdit}
                        onClick={async () => {
                          if (!params.id) return;
                          setSvSaving(true); setSvMsg("");
                          try {
                            await api.put(`/api/employees/${params.id}/site-visibility`, {
                              all_sites: svAllSites, site_ids: svSiteIds,
                            });
                            setSvMsg("Site visibility saved.");
                          } catch (err) {
                            setSvMsg(err instanceof ApiError ? err.message : "Save failed");
                          } finally { setSvSaving(false); }
                        }}
                        className="rounded-lg border border-pme-blue px-3 py-1.5 text-xs font-medium text-pme-blue hover:bg-blue-50 disabled:opacity-50"
                      >
                        {svSaving ? "Saving…" : "Save site visibility"}
                      </button>
                      {svMsg && <span className="text-xs text-gray-600">{svMsg}</span>}
                      {!isEdit && <span className="text-xs text-gray-400">Save the employee first</span>}
                    </div>
                  </div>
                </Field>
              </Row>
            );
          })()}
          <Row>
            <Field label="Contracted hours / week">
              <input
                type="number"
                step="0.5"
                value={form.contract_hours_weekly ?? ""}
                onChange={(e) =>
                  update(
                    "contract_hours_weekly",
                    e.target.value === "" ? null : e.target.value,
                  )
                }
                className="input"
              />
            </Field>
            <Field label="Contracted hours / day">
              <input
                type="number"
                step="0.5"
                value={form.contracted_hours_daily ?? ""}
                onChange={(e) =>
                  update(
                    "contracted_hours_daily",
                    e.target.value === "" ? null : e.target.value,
                  )
                }
                className="input"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Working days">
              <WorkingDaysPicker
                value={form.working_days ?? null}
                onChange={(days) => update("working_days", days)}
              />
            </Field>
          </Row>
          <Row>
            <Field label="Start date">
              <input
                type="date"
                required
                value={form.start_date ?? ""}
                onChange={(e) => update("start_date", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Probation end date">
              <input
                type="date"
                value={form.probation_end_date ?? ""}
                onChange={(e) =>
                  update("probation_end_date", e.target.value)
                }
                className="input"
              />
            </Field>
          </Row>
          <Field label="End date">
            <input
              type="date"
              value={form.end_date ?? ""}
              onChange={(e) => update("end_date", e.target.value)}
              className="input max-w-xs"
            />
          </Field>
        </Section>

        {/* Employment Status History */}
        {mode === "edit" && (
          <Section title="Employment Status History">
            {statusHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Effective Date</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Previous</th>
                      <th className="px-3 py-2">Comment</th>
                      <th className="px-3 py-2">Changed By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {statusHistory.map((h) => (
                      <tr key={h.id}>
                        <td className="px-3 py-2">{h.effective_date}</td>
                        <td className="px-3 py-2 font-medium">{h.employment_status}</td>
                        <td className="px-3 py-2 text-gray-500">{h.previous_status ?? "—"}</td>
                        <td className="px-3 py-2 text-gray-500">{h.comment ?? "—"}</td>
                        <td className="px-3 py-2 text-gray-500">{h.changed_by_name ?? "System"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400">No status history recorded.</p>
            )}
          </Section>
        )}

        {/* Job Information History */}
        {mode === "edit" && (
          <Section title="Job Information History">
            {jobHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Effective Date</th>
                      <th className="px-3 py-2">Job Title</th>
                      <th className="px-3 py-2">Site</th>
                      <th className="px-3 py-2">Department</th>
                      <th className="px-3 py-2">Manager</th>
                      <th className="px-3 py-2">Reason</th>
                      <th className="px-3 py-2">Changed By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {jobHistory.map((h) => (
                      <tr key={h.id}>
                        <td className="px-3 py-2">{h.effective_date}</td>
                        <td className="px-3 py-2 font-medium">{h.job_title ?? "—"}</td>
                        <td className="px-3 py-2 text-gray-500">{h.site_name ?? "—"}</td>
                        <td className="px-3 py-2 text-gray-500">{h.department_name ?? "—"}</td>
                        <td className="px-3 py-2 text-gray-500">{h.manager_name ?? "—"}</td>
                        <td className="px-3 py-2 text-gray-500">{h.change_reason ?? "—"}</td>
                        <td className="px-3 py-2 text-gray-500">{h.changed_by_name ?? "System"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400">No job history recorded.</p>
            )}
          </Section>
        )}

        {/* ----------------------------------------------------------------- */}
        {/* 5. Compensation (gated by permissions)                            */}
        {/* ----------------------------------------------------------------- */}
        {canViewComp && (
          <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Banknote size={18} strokeWidth={1.75} aria-hidden className="text-pme-blue" />
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Compensation
                </h2>
              </div>
              <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200">
                Restricted access
              </span>
            </div>

            {/* Row 1: Pay Schedule + Pay Type */}
            <Row>
              <Field label="Pay schedule">
                {canEditComp ? (
                  <DropdownSelect
                    listCode="pay_schedule"
                    value={form.pay_schedule ?? ""}
                    onChange={(v) => update("pay_schedule", v === "" ? null : (v as PaySchedule))}
                    placeholder="-- Select --"
                  />
                ) : (
                  <CompFieldLabel listCode="pay_schedule" value={form.pay_schedule} />
                )}
              </Field>
              <Field label="Pay type">
                {canEditComp ? (
                  <DropdownSelect
                    listCode="pay_type"
                    value={form.pay_type ?? ""}
                    onChange={(v) => update("pay_type", v === "" ? null : (v as PayType))}
                    placeholder="-- Select --"
                  />
                ) : (
                  <CompFieldLabel listCode="pay_type" value={form.pay_type} />
                )}
              </Field>
            </Row>

            {/* Row 2: Compound pay rate input */}
            <Field label="Pay rate">
              {canEditComp ? (
                <div className="flex items-center gap-0">
                  <span className="flex h-[38px] items-center rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 px-3 text-sm font-medium text-gray-500">
                    {activeSymbol}
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.pay_rate ?? ""}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9.]/g, "");
                      update("pay_rate", v === "" ? null : v);
                    }}
                    placeholder="0.00"
                    className="h-[38px] w-32 border border-gray-300 px-3 text-sm tabular-nums focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                  />
                  <DropdownSelect
                    listCode="currency"
                    value={form.pay_currency ?? currInfo.code}
                    onChange={(v) => update("pay_currency", v)}
                    className="h-[38px] border border-l-0 border-gray-300 bg-gray-50 px-1 text-xs text-gray-600 focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                  />
                  <span className="mx-2 text-xs text-gray-400">per</span>
                  <DropdownSelect
                    listCode="pay_frequency"
                    value={form.pay_frequency ?? "year"}
                    onChange={(v) => update("pay_frequency", v as PayFrequency)}
                    className="h-[38px] rounded-r-lg border border-gray-300 px-2 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
                  />
                </div>
              ) : (
                <p className="text-sm text-gray-800">
                  {form.pay_rate
                    ? `${activeSymbol}${parseFloat(form.pay_rate).toLocaleString(activeLocale, { minimumFractionDigits: 2 })} ${activeCurrency} ${form.pay_frequency ? formatPayFrequency(form.pay_frequency) : ""}`
                    : "--"}
                </p>
              )}
            </Field>

            {/* Row 3: Contracted hours / week */}
            <Field label="Contracted hours per week">
              {canEditComp ? (
                <input
                  type="number"
                  step="0.5"
                  value={form.contract_hours_weekly ?? ""}
                  onChange={(e) =>
                    update("contract_hours_weekly", e.target.value === "" ? null : e.target.value)
                  }
                  placeholder="37.5"
                  className="input max-w-[120px]"
                />
              ) : (
                <p className="text-sm text-gray-800">
                  {form.contract_hours_weekly ?? "--"}
                </p>
              )}
            </Field>

            {/* Derived values summary */}
            {annualSalary > 0 && (
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-gray-100 bg-gray-50/50 p-3 text-xs text-gray-500 md:grid-cols-4">
                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-gray-400">Yearly</span>
                  <span className="font-medium text-gray-800">{fmt(annualSalary)}</span>
                </div>
                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-gray-400">Monthly</span>
                  <span className="font-medium text-gray-800">{fmt(annualSalary / 12)}</span>
                </div>
                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-gray-400">Weekly</span>
                  <span className="font-medium text-gray-800">{fmt(annualSalary / 52)}</span>
                </div>
                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-gray-400">Hourly</span>
                  <span className="font-medium text-gray-800">{fmt(annualSalary / 52 / weeklyHrs)}</span>
                </div>
              </div>
            )}

            {/* Tax sub-region selector (US states, ES autonomous communities) */}
            {canEditComp && availableSubRegions.length > 0 && (
              <Field label={selectedRegion?.code === "us" ? "Tax state" : selectedRegion?.code === "es" ? "Comunidad autónoma" : "Tax sub-region"}>
                <select
                  value={form.tax_sub_region ?? ""}
                  onChange={(e) => update("tax_sub_region", e.target.value || null)}
                  className="input max-w-xs"
                >
                  <option value="">-- None --</option>
                  {availableSubRegions.map((sr) => (
                    <option key={sr.code} value={sr.code}>
                      {sr.code.toUpperCase()}{sr.name && sr.name !== sr.code ? ` — ${sr.name.replace(/^.*—\s*/, "")}` : ""}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {!canEditComp && form.tax_sub_region && (
              <Field label={selectedRegion?.code === "us" ? "Tax state" : "Tax sub-region"}>
                <p className="text-sm text-gray-800">{form.tax_sub_region.toUpperCase()}</p>
              </Field>
            )}

            {/* Tax deduction table */}
            {annualSalary > 0 && taxBreakdown && (
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Tax deductions{taxYear ? ` (${taxYear})` : ""}
                </h3>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-[13px]">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                        <th className="px-3 py-2.5"></th>
                        <th className="px-3 py-2.5 text-right">Yearly</th>
                        <th className="px-3 py-2.5 text-right">Monthly</th>
                        <th className="px-3 py-2.5 text-right">Weekly</th>
                        <th className="px-3 py-2.5 text-right">Daily</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="px-3 py-2.5 font-medium text-gray-700">Gross income</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmt(taxBreakdown.gross.yearly)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmt(taxBreakdown.gross.monthly)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmt(taxBreakdown.gross.weekly)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmt(taxBreakdown.gross.daily)}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="px-3 py-2.5 font-medium text-gray-700">Taxable income</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmt(taxBreakdown.taxableIncome.yearly)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmt(taxBreakdown.taxableIncome.monthly)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmt(taxBreakdown.taxableIncome.weekly)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmt(taxBreakdown.taxableIncome.daily)}</td>
                      </tr>
                      {taxBreakdown.lines.map((line, i) => (
                        <tr key={i} className={i === taxBreakdown.lines.length - 1 ? "border-b border-gray-200" : "border-b border-gray-100"}>
                          <td className="px-3 py-2.5 font-medium text-gray-700">{line.label}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-red-600">{fmt(line.row.yearly)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-red-600">{fmt(line.row.monthly)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-red-600">{fmt(line.row.weekly)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-red-600">{fmt(line.row.daily)}</td>
                        </tr>
                      ))}
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <td className="px-3 py-2.5 font-semibold text-gray-900">Take-home pay</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-green-700">{fmt(taxBreakdown.takeHome.yearly)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-green-700">{fmt(taxBreakdown.takeHome.monthly)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-green-700">{fmt(taxBreakdown.takeHome.weekly)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-green-700">{fmt(taxBreakdown.takeHome.daily)}</td>
                      </tr>
                      {taxBreakdown.employerLines.map((line, i) => (
                        <tr key={`er-${i}`}>
                          <td className="px-3 py-2.5 italic text-gray-400">{line.label}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums italic text-gray-400">{fmt(line.row.yearly)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums italic text-gray-400">{fmt(line.row.monthly)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums italic text-gray-400">{fmt(line.row.weekly)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums italic text-gray-400">{fmt(line.row.daily)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-600">
                  Total employer cost: <span className="font-semibold text-gray-900">{fmt(taxBreakdown.totalEmployerCostYearly)}</span> / year
                </div>

                <p className="text-[11px] text-gray-400">
                  Estimates only. Based on {selectedRegion?.name ?? "region"}{form.tax_sub_region ? ` / ${form.tax_sub_region.toUpperCase()}` : ""} {taxYear || "current"} rates.
                  Does not account for student loans, pensions, or other deductions.
                </p>
              </div>
            )}
            {annualSalary > 0 && !taxConfig && !taxConfigLoading && (
              <p className="rounded-lg bg-amber-50 px-4 py-3 text-xs text-amber-700">
                Tax brackets not configured for this region. Configure in Settings → Tax Brackets.
              </p>
            )}
          </section>
        )}

        {/* ----------------------------------------------------------------- */}
        {/* 5b. Bank Details (gated by bank detail permissions)               */}
        {/* ----------------------------------------------------------------- */}
        {canViewBank && mode === "edit" && (
          <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Bank Details
                </h2>
              </div>
              <div className="flex items-center gap-2">
                {bankDetails?.is_verified ? (
                  <span className="rounded-full bg-green-50 px-2.5 py-0.5 text-[11px] font-medium text-green-700 ring-1 ring-green-200">
                    Verified{bankDetails.verified_by_name ? ` by ${bankDetails.verified_by_name}` : ""}
                    {bankDetails.verified_at ? ` on ${new Date(bankDetails.verified_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}
                  </span>
                ) : bankDetails ? (
                  <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200">
                    Unverified
                  </span>
                ) : null}
                <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200">
                  Restricted access
                </span>
                {!bankEditing && canEditBank && (
                  <button
                    type="button"
                    disabled={bankUnmasking}
                    onClick={async () => {
                      setBankUnmasking(true);
                      setBankNotice(null);
                      try {
                        const unmasked = await api.get<BankDetails>(`/api/employees/${form.id}/bank-details/unmasked`);
                        if (unmasked && "id" in unmasked) {
                          setBankForm({
                            account_name: unmasked.account_name ?? "",
                            bank_name: unmasked.bank_name ?? "",
                            sort_code: unmasked.sort_code ?? "",
                            account_number_uk: unmasked.account_number_uk ?? "",
                            building_society_reference: unmasked.building_society_reference ?? "",
                            branch_code: unmasked.branch_code ?? "",
                            account_number_za: unmasked.account_number_za ?? "",
                            account_type_za: unmasked.account_type_za ?? "",
                            iban: unmasked.iban ?? "",
                            bic_swift: unmasked.bic_swift ?? "",
                            routing_number: unmasked.routing_number ?? "",
                            account_number_us: unmasked.account_number_us ?? "",
                            account_type_us: unmasked.account_type_us ?? "",
                          });
                        } else {
                          setBankForm({
                            account_name: "", bank_name: "", sort_code: "", account_number_uk: "",
                            building_society_reference: "", branch_code: "", account_number_za: "",
                            account_type_za: "", iban: "", bic_swift: "", routing_number: "",
                            account_number_us: "", account_type_us: "",
                          });
                        }
                        setBankEditing(true);
                      } catch (err) {
                        setBankNotice(err instanceof ApiError ? err.message : "Failed to load bank details for editing");
                      } finally {
                        setBankUnmasking(false);
                      }
                    }}
                    className="rounded-lg border border-pme-blue px-3 py-1 text-xs font-semibold text-pme-blue"
                  >
                    {bankUnmasking ? "Loading..." : "Edit"}
                  </button>
                )}
                {bankEditing && (
                  <button
                    type="button"
                    onClick={() => {
                      setBankEditing(false);
                      setBankForm({});
                      setBankNotice(null);
                    }}
                    className="rounded-lg border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-600"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            {!bankEditing ? (
              /* Display mode — always masked */
              <div className="space-y-3">
                {bankDetails?.corrupted_fields && bankDetails.corrupted_fields.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Some bank detail values may need to be re-entered. Click Edit to update them.
                  </div>
                )}
                <Row>
                  <Field label="Account Name">
                    <p className="text-sm text-gray-800">{bankDetails?.account_name || "—"}</p>
                  </Field>
                  {selectedRegion?.code !== "uk" && (
                    <Field label="Bank Name">
                      <p className="text-sm text-gray-800">{bankDetails?.bank_name || "—"}</p>
                    </Field>
                  )}
                </Row>
                {selectedRegion?.code === "uk" && (
                  <Row>
                    <Field label="Sort Code">
                      <p className="text-sm text-gray-800 font-mono">{bankDetails?.sort_code || "—"}</p>
                    </Field>
                    <Field label="Account Number">
                      <p className="text-sm text-gray-800 font-mono">{bankDetails?.account_number_uk || "—"}</p>
                    </Field>
                  </Row>
                )}
                {selectedRegion?.code === "za" && (
                  <Row>
                    <Field label="Branch Code">
                      <p className="text-sm text-gray-800 font-mono">{bankDetails?.branch_code || "—"}</p>
                    </Field>
                    <Field label="Account Number">
                      <p className="text-sm text-gray-800 font-mono">{bankDetails?.account_number_za || "—"}</p>
                    </Field>
                  </Row>
                )}
                {selectedRegion?.code === "es" && (
                  <Row>
                    <Field label="IBAN">
                      <p className="text-sm text-gray-800 font-mono">{bankDetails?.iban || "—"}</p>
                    </Field>
                    <Field label="BIC / SWIFT">
                      <p className="text-sm text-gray-800">{bankDetails?.bic_swift || "—"}</p>
                    </Field>
                  </Row>
                )}
                {selectedRegion?.code === "us" && (
                  <Row>
                    <Field label="Routing Number">
                      <p className="text-sm text-gray-800 font-mono">{bankDetails?.routing_number || "—"}</p>
                    </Field>
                    <Field label="Account Number">
                      <p className="text-sm text-gray-800 font-mono">{bankDetails?.account_number_us || "—"}</p>
                    </Field>
                  </Row>
                )}
                {!bankDetails && (
                  <p className="text-sm text-gray-400 italic">No bank details on file.</p>
                )}
                {bankDetails && !bankDetails.is_verified && (currentUser?.is_admin || isSuperAdmin) && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await api.post(`/api/employees/${form.id}/bank-details/verify`);
                        setBankNotice("Bank details verified.");
                        const bd = await api.get<BankDetails>(`/api/employees/${form.id}/bank-details`);
                        if (bd && "id" in bd) setBankDetails(bd);
                      } catch (err) {
                        setBankNotice(err instanceof ApiError ? err.message : "Failed to verify");
                      }
                    }}
                    className="rounded-lg border border-green-300 px-4 py-2 text-sm font-semibold text-green-700"
                  >
                    Mark as Verified
                  </button>
                )}
                {bankNotice && <span className="text-xs text-gray-600">{bankNotice}</span>}
              </div>
            ) : (
              /* Edit mode — unmasked values in form */
              <>
            <Row>
              <Field label="Account Name">
                  <input
                    value={bankForm.account_name ?? ""}
                    onChange={(e) => setBankForm((p) => ({ ...p, account_name: e.target.value }))}
                    className="input"
                  />
              </Field>
              {selectedRegion?.code !== "uk" && (
                <Field label="Bank Name">
                    <input
                      value={bankForm.bank_name ?? ""}
                      onChange={(e) => setBankForm((p) => ({ ...p, bank_name: e.target.value }))}
                      className="input"
                    />
                </Field>
              )}
            </Row>

            {selectedRegion?.code === "uk" && (
              <Row>
                <Field label="Sort Code">
                    <input
                      value={bankForm.sort_code ?? ""}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9-]/g, "").slice(0, 8);
                        setBankForm((p) => ({ ...p, sort_code: v }));
                      }}
                      placeholder="XX-XX-XX"
                      className="input max-w-[140px]"
                    />
                </Field>
                <Field label="Account Number">
                    <input
                      value={bankForm.account_number_uk ?? ""}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, "").slice(0, 8);
                        setBankForm((p) => ({ ...p, account_number_uk: v }));
                      }}
                      placeholder="12345678"
                      maxLength={8}
                      className="input max-w-[160px]"
                    />
                </Field>
                <Field label="Building Society Ref">
                    <input
                      value={bankForm.building_society_reference ?? ""}
                      onChange={(e) => setBankForm((p) => ({ ...p, building_society_reference: e.target.value }))}
                      className="input"
                    />
                </Field>
              </Row>
            )}

            {selectedRegion?.code === "za" && (
              <Row>
                <Field label="Branch Code">
                    <input
                      value={bankForm.branch_code ?? ""}
                      onChange={(e) => setBankForm((p) => ({ ...p, branch_code: e.target.value }))}
                      className="input max-w-[140px]"
                    />
                </Field>
                <Field label="Account Number">
                    <input
                      value={bankForm.account_number_za ?? ""}
                      onChange={(e) => setBankForm((p) => ({ ...p, account_number_za: e.target.value }))}
                      className="input"
                    />
                </Field>
                <Field label="Account Type">
                    <select
                      value={bankForm.account_type_za ?? ""}
                      onChange={(e) => setBankForm((p) => ({ ...p, account_type_za: e.target.value }))}
                      className="input"
                    >
                      <option value="">-- Select --</option>
                      <option value="Cheque">Cheque Account</option>
                      <option value="Savings">Savings Account</option>
                      <option value="Transmission">Transmission Account</option>
                    </select>
                </Field>
              </Row>
            )}

            {selectedRegion?.code === "es" && (
              <Row>
                <Field label="IBAN">
                    <input
                      value={bankForm.iban ?? ""}
                      onChange={(e) => setBankForm((p) => ({ ...p, iban: e.target.value.toUpperCase() }))}
                      placeholder="ESXX XXXX XXXX XXXX XXXX XXXX"
                      className="input"
                    />
                </Field>
                <Field label="BIC / SWIFT">
                    <input
                      value={bankForm.bic_swift ?? ""}
                      onChange={(e) => setBankForm((p) => ({ ...p, bic_swift: e.target.value.toUpperCase() }))}
                      className="input max-w-[180px]"
                    />
                </Field>
              </Row>
            )}

            {selectedRegion?.code === "us" && (
              <Row>
                <Field label="Routing Number">
                    <input
                      value={bankForm.routing_number ?? ""}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, "").slice(0, 9);
                        setBankForm((p) => ({ ...p, routing_number: v }));
                      }}
                      maxLength={9}
                      className="input max-w-[160px]"
                    />
                </Field>
                <Field label="Account Number">
                    <input
                      value={bankForm.account_number_us ?? ""}
                      onChange={(e) => setBankForm((p) => ({ ...p, account_number_us: e.target.value }))}
                      className="input"
                    />
                </Field>
                <Field label="Account Type">
                    <select
                      value={bankForm.account_type_us ?? ""}
                      onChange={(e) => setBankForm((p) => ({ ...p, account_type_us: e.target.value }))}
                      className="input"
                    >
                      <option value="">-- Select --</option>
                      <option value="Checking">Checking</option>
                      <option value="Savings">Savings</option>
                    </select>
                </Field>
              </Row>
            )}

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  disabled={bankSaving}
                  onClick={async () => {
                    setBankSaving(true);
                    setBankNotice(null);
                    try {
                      const cleanBank: Record<string, string> = {};
                      for (const [k, v] of Object.entries(bankForm)) {
                        if (!isMaskedValue(v)) cleanBank[k] = v;
                      }
                      const res = await api.put<BankDetails>(
                        `/api/employees/${form.id}/bank-details`,
                        cleanBank,
                      );
                      setBankDetails(res);
                      setBankNotice("Bank details saved.");
                      setBankEditing(false);
                      setBankForm({});
                    } catch (err) {
                      setBankNotice(err instanceof ApiError ? err.message : "Failed to save");
                    } finally {
                      setBankSaving(false);
                    }
                  }}
                  className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {bankSaving ? "Saving..." : "Save Bank Details"}
                </button>
                {bankNotice && (
                  <span className="text-xs text-gray-600">{bankNotice}</span>
                )}
              </div>
              </>
            )}
          </section>
        )}

        {/* Deductions */}
        {mode === "edit" && canManageDeductions && (
          <Section title={t("employeeForm.deductions", "Deductions")}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase text-gray-500">{t("employeeForm.deductions", "Deductions")}</h3>
                {canManageDeductions && !addingDeduction && (
                  <button type="button" onClick={() => setAddingDeduction(true)} className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white">
                    + {t("employeeForm.addDeduction", "Add Deduction")}
                  </button>
                )}
              </div>
              {addingDeduction && (
                <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                  <input type="text" placeholder={t("employeeForm.deductionName", "Name")} value={deductionForm.name} onChange={(e) => setDeductionForm({ ...deductionForm, name: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
                  <DropdownSelect listCode="deduction_type" value={deductionForm.deduction_type} onChange={(v) => setDeductionForm({ ...deductionForm, deduction_type: v })} placeholder={t("employeeForm.deductionType", "Deduction type")} />
                  <div className="grid grid-cols-3 gap-2">
                    <select value={deductionForm.amount_type} onChange={(e) => setDeductionForm({ ...deductionForm, amount_type: e.target.value as "fixed" | "percentage" })} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
                      <option value="fixed">{t("employeeForm.fixed", "Fixed")}</option>
                      <option value="percentage">{t("employeeForm.percentage", "Percentage")}</option>
                    </select>
                    <input type="number" step="0.01" placeholder={t("employeeForm.amount", "Amount")} value={deductionForm.amount} onChange={(e) => setDeductionForm({ ...deductionForm, amount: e.target.value })} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
                    <select value={deductionForm.contribution_type} onChange={(e) => setDeductionForm({ ...deductionForm, contribution_type: e.target.value as "pre_tax" | "post_tax" })} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
                      <option value="pre_tax">{t("employeeForm.preTax", "Pre-tax")}</option>
                      <option value="post_tax">{t("employeeForm.postTax", "Post-tax")}</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="date" placeholder={t("employeeForm.effectiveFrom", "From")} value={deductionForm.effective_from} onChange={(e) => setDeductionForm({ ...deductionForm, effective_from: e.target.value })} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
                    <input type="date" placeholder={t("employeeForm.effectiveTo", "To")} value={deductionForm.effective_to} onChange={(e) => setDeductionForm({ ...deductionForm, effective_to: e.target.value })} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => void addDeduction()} disabled={deductionSaving} className="rounded-lg bg-pme-green px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">{t("common.save", "Save")}</button>
                    <button type="button" onClick={() => setAddingDeduction(false)} className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700">{t("common.cancel", "Cancel")}</button>
                  </div>
                </div>
              )}
              {deductions.length > 0 ? (
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-gray-500 border-b"><th className="pb-1">{t("employeeForm.deductionName", "Name")}</th><th className="pb-1">{t("employeeForm.type", "Type")}</th><th className="pb-1">{t("employeeForm.amount", "Amount")}</th><th className="pb-1">{t("employeeForm.timing", "Timing")}</th><th className="pb-1">{t("employeeForm.status", "Status")}</th>{canManageDeductions && <th />}</tr></thead>
                  <tbody>
                    {deductions.map((d) => (
                      <tr key={d.id} className="border-b border-gray-100">
                        <td className="py-1.5">{d.name}</td>
                        <td className="py-1.5">{d.deduction_type}</td>
                        <td className="py-1.5">{d.amount_type === "percentage" ? `${d.amount}%` : `${d.currency || ""} ${d.amount}`}</td>
                        <td className="py-1.5">{d.contribution_type === "pre_tax" ? t("employeeForm.preTax", "Pre-tax") : t("employeeForm.postTax", "Post-tax")}</td>
                        <td className="py-1.5">{d.is_active ? t("common.active", "Active") : t("common.inactive", "Inactive")}</td>
                        {canManageDeductions && <td className="py-1.5 text-right"><button type="button" onClick={() => void deleteDeduction(d.id)} className="text-red-500 hover:text-red-700">x</button></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-xs text-gray-400">{t("employeeForm.noDeductions", "No deductions recorded.")}</p>
              )}
            </div>
          </Section>
        )}

        {/* Benefits */}
        {mode === "edit" && canManageDeductions && (
          <Section title={t("employeeForm.benefits", "Benefits")}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase text-gray-500">{t("employeeForm.benefits", "Benefits")}</h3>
                {canManageDeductions && !addingBenefit && (
                  <button type="button" onClick={() => setAddingBenefit(true)} className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white">
                    + {t("employeeForm.addBenefit", "Add Benefit")}
                  </button>
                )}
              </div>
              {addingBenefit && (
                <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                  <select value={benefitForm.benefit_plan_id} onChange={(e) => setBenefitForm({ ...benefitForm, benefit_plan_id: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
                    <option value="">{t("employeeForm.selectBenefitPlan", "Select benefit plan...")}</option>
                    {benefitPlans.filter((p) => p.is_active).map((p) => (
                      <option key={p.id} value={p.id}>{p.name} ({p.benefit_type})</option>
                    ))}
                  </select>
                  <input type="date" value={benefitForm.enrolled_date} onChange={(e) => setBenefitForm({ ...benefitForm, enrolled_date: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => void addBenefit()} disabled={benefitSaving} className="rounded-lg bg-pme-green px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">{t("common.save", "Save")}</button>
                    <button type="button" onClick={() => setAddingBenefit(false)} className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700">{t("common.cancel", "Cancel")}</button>
                  </div>
                </div>
              )}
              {benefits.length > 0 ? (
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-gray-500 border-b"><th className="pb-1">{t("employeeForm.plan", "Plan")}</th><th className="pb-1">{t("employeeForm.enrolled", "Enrolled")}</th><th className="pb-1">{t("employeeForm.status", "Status")}</th>{canManageDeductions && <th />}</tr></thead>
                  <tbody>
                    {benefits.map((b) => {
                      const plan = benefitPlans.find((p) => p.id === b.benefit_plan_id);
                      return (
                        <tr key={b.id} className="border-b border-gray-100">
                          <td className="py-1.5">{plan?.name ?? `Plan #${b.benefit_plan_id}`}</td>
                          <td className="py-1.5">{b.enrolled_date}</td>
                          <td className="py-1.5">{b.is_active ? t("common.active", "Active") : t("common.inactive", "Inactive")}</td>
                          {canManageDeductions && <td className="py-1.5 text-right"><button type="button" onClick={() => void deleteBenefit(b.id)} className="text-red-500 hover:text-red-700">x</button></td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="text-xs text-gray-400">{t("employeeForm.noBenefits", "No benefits enrolled.")}</p>
              )}
            </div>
          </Section>
        )}

        {/* Bonuses & Commissions */}
        {mode === "edit" && canViewBonuses && (
          <Section title="Bonuses & Commissions">
            <div className="space-y-3">
              {/* Bonuses */}
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase text-gray-500">Bonuses</h3>
                {canEditBonuses && !addingBonus && (
                  <button
                    type="button"
                    onClick={() => setAddingBonus(true)}
                    className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white"
                  >
                    + Add Bonus
                  </button>
                )}
                {addingBonus && (
                  <button
                    type="button"
                    onClick={() => setAddingBonus(false)}
                    className="text-xs text-gray-500"
                  >
                    Cancel
                  </button>
                )}
              </div>
              {addingBonus && (
                <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/40 p-3">
                  <Row>
                    <Field label="Date">
                      <input
                        type="date"
                        value={bonusForm.date}
                        onChange={(e) => setBonusForm({ ...bonusForm, date: e.target.value })}
                        className="input"
                        required
                      />
                    </Field>
                    <Field label="Amount">
                      <input
                        type="number"
                        step="0.01"
                        value={bonusForm.amount}
                        onChange={(e) => setBonusForm({ ...bonusForm, amount: e.target.value })}
                        className="input"
                        required
                      />
                    </Field>
                    <Field label="Currency">
                      <input
                        value={bonusForm.currency}
                        onChange={(e) => setBonusForm({ ...bonusForm, currency: e.target.value })}
                        className="input max-w-[80px]"
                      />
                    </Field>
                  </Row>
                  <Row>
                    <Field label="Reason">
                      <input
                        value={bonusForm.reason}
                        onChange={(e) => setBonusForm({ ...bonusForm, reason: e.target.value })}
                        className="input"
                      />
                    </Field>
                    <Field label="Comment">
                      <input
                        value={bonusForm.comment}
                        onChange={(e) => setBonusForm({ ...bonusForm, comment: e.target.value })}
                        className="input"
                      />
                    </Field>
                  </Row>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setAddingBonus(false)}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => void addBonus()}
                      disabled={bonusSaving || !bonusForm.date || !bonusForm.amount}
                      className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                    >
                      {bonusSaving ? "Saving..." : "Add"}
                    </button>
                  </div>
                </div>
              )}
              {bonuses.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">Amount</th>
                        <th className="px-3 py-2">Reason</th>
                        <th className="px-3 py-2">Comment</th>
                        {canEditBonuses && <th className="px-3 py-2"></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {bonuses.map((b) => (
                        <tr key={b.id}>
                          <td className="px-3 py-2">{b.date}</td>
                          <td className="px-3 py-2 font-medium">{b.currency} {b.amount}</td>
                          <td className="px-3 py-2 text-gray-500">{b.reason ?? "—"}</td>
                          <td className="px-3 py-2 text-gray-500">{b.comment ?? "—"}</td>
                          {canEditBonuses && (
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => void deleteBonus(b.id)}
                                className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-600"
                              >
                                Delete
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-400">No bonuses recorded.</p>
              )}

              {/* Commissions */}
              <div className="flex items-center justify-between pt-2">
                <h3 className="text-xs font-semibold uppercase text-gray-500">Commissions</h3>
                {canEditBonuses && !addingCommission && (
                  <button
                    type="button"
                    onClick={() => setAddingCommission(true)}
                    className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white"
                  >
                    + Add Commission
                  </button>
                )}
                {addingCommission && (
                  <button
                    type="button"
                    onClick={() => setAddingCommission(false)}
                    className="text-xs text-gray-500"
                  >
                    Cancel
                  </button>
                )}
              </div>
              {addingCommission && (
                <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/40 p-3">
                  <Row>
                    <Field label="Date">
                      <input
                        type="date"
                        value={commissionForm.date}
                        onChange={(e) => setCommissionForm({ ...commissionForm, date: e.target.value })}
                        className="input"
                        required
                      />
                    </Field>
                    <Field label="Amount">
                      <input
                        type="number"
                        step="0.01"
                        value={commissionForm.amount}
                        onChange={(e) => setCommissionForm({ ...commissionForm, amount: e.target.value })}
                        className="input"
                        required
                      />
                    </Field>
                    <Field label="Currency">
                      <input
                        value={commissionForm.currency}
                        onChange={(e) => setCommissionForm({ ...commissionForm, currency: e.target.value })}
                        className="input max-w-[80px]"
                      />
                    </Field>
                  </Row>
                  <Row>
                    <Field label="Description">
                      <input
                        value={commissionForm.description}
                        onChange={(e) => setCommissionForm({ ...commissionForm, description: e.target.value })}
                        className="input"
                      />
                    </Field>
                    <Field label="Comment">
                      <input
                        value={commissionForm.comment}
                        onChange={(e) => setCommissionForm({ ...commissionForm, comment: e.target.value })}
                        className="input"
                      />
                    </Field>
                  </Row>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setAddingCommission(false)}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => void addCommission()}
                      disabled={commissionSaving || !commissionForm.date || !commissionForm.amount}
                      className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                    >
                      {commissionSaving ? "Saving..." : "Add"}
                    </button>
                  </div>
                </div>
              )}
              {commissions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">Amount</th>
                        <th className="px-3 py-2">Description</th>
                        <th className="px-3 py-2">Comment</th>
                        {canEditBonuses && <th className="px-3 py-2"></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {commissions.map((c) => (
                        <tr key={c.id}>
                          <td className="px-3 py-2">{c.date}</td>
                          <td className="px-3 py-2 font-medium">{c.currency} {c.amount}</td>
                          <td className="px-3 py-2 text-gray-500">{c.description ?? "—"}</td>
                          <td className="px-3 py-2 text-gray-500">{c.comment ?? "—"}</td>
                          {canEditBonuses && (
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => void deleteCommission(c.id)}
                                className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-600"
                              >
                                Delete
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-400">No commissions recorded.</p>
              )}
            </div>
          </Section>
        )}

        {/* ----------------------------------------------------------------- */}
        {/* 6. Emergency Contact                                              */}
        {/* ----------------------------------------------------------------- */}
        <Section title="Emergency Contact">
          <Row>
            <Field label="Contact name">
              <input
                value={form.emergency_contact_name ?? ""}
                onChange={(e) =>
                  update("emergency_contact_name", e.target.value)
                }
                className="input"
              />
            </Field>
            <Field label="Contact phone">
              <input
                type="tel"
                value={form.emergency_contact_phone ?? ""}
                onChange={(e) =>
                  update("emergency_contact_phone", e.target.value)
                }
                className="input"
              />
            </Field>
            <Field label="Relationship">
              <DropdownSelect
                listCode="emergency_relationship"
                value={form.emergency_contact_relationship ?? ""}
                onChange={(v) => update("emergency_contact_relationship", v)}
                placeholder="Select..."
                className="input"
              />
            </Field>
          </Row>
        </Section>

        {/* ----------------------------------------------------------------- */}
        {/* 7. System Access                                                  */}
        {/* ----------------------------------------------------------------- */}
        <Section title="System Access">
          <Field
            label="Auth method"
            hint={
              form.auth_method === "pin"
                ? "Employee will use PIN + device UUID binding. Less secure than passkey."
                : form.auth_method === "passkey" &&
                    passkeys.length === 0 &&
                    mode === "edit"
                  ? "Employee has no passkey yet -- they'll be prompted to register one on next login."
                  : undefined
            }
          >
            <DropdownSelect
              listCode="auth_method"
              value={form.auth_method ?? "both"}
              onChange={(v) => update("auth_method", v as AuthMethod)}
              className="input max-w-xs"
            />
          </Field>

          {mode === "edit" && (
            <Field label="Registered passkeys">
              {passkeys.length === 0 ? (
                <p className="text-xs text-gray-400">None registered.</p>
              ) : (
                <ul className="space-y-2">
                  {passkeys.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {p.device_name}
                        </p>
                        <p className="text-[11px] text-gray-500">
                          Registered{" "}
                          {new Date(p.created_at).toLocaleDateString()}
                          {p.last_used_at &&
                            ` · Last used ${new Date(p.last_used_at).toLocaleDateString()}`}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePasskey(p.id)}
                        className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-600"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Field>
          )}

          {mode === "edit" && (
            <Field label="Device binding">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-700">
                  {form.device_fingerprint
                    ? `Device bound${
                        form.device_bound_at
                          ? ` on ${new Date(form.device_bound_at).toLocaleDateString()}`
                          : ""
                      }`
                    : "No device bound"}
                </span>
                {form.device_fingerprint && (
                  <button
                    type="button"
                    onClick={unbindDevice}
                    className="rounded-lg border border-gray-300 px-3 py-1 text-xs"
                  >
                    Unbind
                  </button>
                )}
              </div>
            </Field>
          )}

          {mode === "edit" && (
            <Field label="PIN">
              <button
                type="button"
                onClick={resetPin}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                Reset PIN
              </button>
            </Field>
          )}

          {isSuperAdmin && mode === "edit" && params.id && (
            <Field
              label="Data export / erasure"
              hint="Exports cover GDPR Article 15. Erasure is permanent and blocked while data is under regional retention."
            >
              <div className="flex flex-wrap gap-2">
                <a
                  href={`/api/employees/${params.id}/export`}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  Export JSON
                </a>
                <a
                  href={`/api/employees/${params.id}/export/csv`}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  Export CSV (ZIP)
                </a>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700"
                >
                  Delete Employee Data
                </button>
              </div>
            </Field>
          )}
        </Section>

        {/* ----------------------------------------------------------------- */}
        {/* 8. Notes                                                          */}
        {/* ----------------------------------------------------------------- */}
        <Section title="Notes">
          <Field label="HR notes">
            <textarea
              value={form.notes ?? ""}
              onChange={(e) => update("notes", e.target.value)}
              rows={4}
              className="input"
            />
          </Field>
        </Section>

        <div className="flex items-center justify-between pt-2">
          <div>
            {mode === "edit" && (
              <button
                type="button"
                onClick={deactivate}
                className="rounded-lg border border-red-300 px-3 py-2 text-sm text-red-600"
              >
                Deactivate
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-pme-blue px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {submitting ? "Saving..." : mode === "create" ? "Create" : "Save"}
            </button>
          </div>
        </div>
      </form>
    </>
  );

  const purgeModalJsx =
    showDeleteModal && form.id != null ? (
      <PurgeModal
        employee={form as Employee}
        onClose={() => setShowDeleteModal(false)}
        onDeleted={() => navigate("/admin/employees")}
      />
    ) : null;

  // -------------------------------------------------------------------------
  // Create mode: single scroll, same as before.
  // -------------------------------------------------------------------------
  if (!isEdit) {
    return (
      <div className="max-w-4xl space-y-6">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
        </div>
        {profileFormJsx}
        {purgeModalJsx}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Edit mode: persistent header + sidebar + tabs.
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-4">
      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
      <button
        type="button"
        onClick={() => {
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate("/admin/employees");
          }
        }}
        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
      >
        <span aria-hidden="true">&larr;</span> Back to Employees
      </button>
      <PersistentHeader
        employee={form}
        sites={sites}
        departments={departments}
        onRefresh={() => {
          if (params.id) {
            void api.get<Employee>(`/api/employees/${params.id}`).then(setForm);
          }
        }}
      />

      {params.id && (
        <div className="flex justify-end">
          <ResendInviteButton
            employeeId={Number(params.id)}
            employeeEmail={form.email ?? ""}
          />
        </div>
      )}

      <div className="flex gap-6">
        <aside className="hidden lg:block w-[250px] shrink-0">
          <VitalsSidebar
            employee={form}
            sites={sites}
            departments={departments}
            directReports={directReports}
          />
        </aside>

        <div className="flex-1 min-w-0 space-y-4">
          <TabBar activeTab={activeTab} onChange={setActiveTab} />

          {activeTab === "profile" && profileFormJsx}
          {activeTab === "documents" && (
            <DocumentsTab employeeId={form.id} />
          )}
          {activeTab === "timeoff" && form.id != null && (
            <EmployeeLeaveManagement employeeId={form.id} />
          )}
          {activeTab === "timeoff" && form.id == null && (
            <p className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500">
              Save the employee before managing leave.
            </p>
          )}
          {activeTab === "training" && (
            <TrainingTab employeeId={form.id} />
          )}
          {activeTab === "performance" && (
            <PerformanceTab employeeId={form.id} employeeName={`${form.first_name ?? ""} ${form.last_name ?? ""}`.trim()} />
          )}
        </div>
      </div>

      {purgeModalJsx}
    </div>
  );
}

function ResendInviteButton({
  employeeId,
  employeeEmail,
}: {
  employeeId: number;
  employeeEmail: string;
}) {
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    new_pin: string;
    email_sent: boolean;
    email_paused?: boolean;
  } | null>(null);

  const send = async () => {
    setSubmitting(true);
    try {
      const res = await api.post<{
        new_pin: string;
        email_sent: boolean;
        email_paused?: boolean;
      }>(`/api/employees/${employeeId}/resend-invite`);
      setResult(res);
      setConfirming(false);
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
        <p>
          {result.email_sent
            ? `Invite sent to ${employeeEmail}.`
            : result.email_paused
              ? `Email is paused, so no invite was sent. The PIN was reset: share it with them yourself.`
              : `Email failed but PIN was reset.`}
        </p>
        <p className="mt-1 font-semibold">New PIN: {result.new_pin}</p>
        <button
          type="button"
          onClick={() => setResult(null)}
          className="mt-2 text-xs text-pme-blue"
        >
          Dismiss
        </button>
      </div>
    );
  }

  if (confirming) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-600">
          Generate new PIN and email {employeeEmail}?
        </span>
        <button
          type="button"
          onClick={send}
          disabled={submitting}
          className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        >
          {submitting ? "Sending..." : "Confirm & Send"}
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="text-xs text-gray-500"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
    >
      Resend Invite
    </button>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-6">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="block font-medium text-gray-700">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </label>
  );
}

function CompFieldLabel({ listCode, value }: { listCode: string; value: string | null | undefined }) {
  const options = useDropdownOptions(listCode);
  const label = options.find((o) => o.value === value)?.label ?? "--";
  return <p className="text-sm text-gray-800">{label}</p>;
}

// --- Purge modal ------------------------------------------------------------

function PurgeModal({
  employee,
  onClose,
  onDeleted,
}: {
  employee: Employee;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [typedNumber, setTypedNumber] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    typedNumber === employee.employee_number && reason.trim().length > 0;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.delete(`/api/employees/${employee.id}/purge`, {
        body: { confirm: true, reason: reason.trim() },
      });
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Delete failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900">
          Delete employee data
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          This will permanently delete all data for{" "}
          <span className="font-semibold">
            {employee.first_name} {employee.last_name}
          </span>{" "}
          ({employee.employee_number}). This action cannot be undone.
        </p>
        <p className="mt-2 text-xs text-gray-500">
          If records are under legal retention the server will refuse and tell
          you when the earliest eligible purge date is.
        </p>

        <div className="mt-4 space-y-3 text-sm">
          <label className="block">
            <span className="text-xs font-medium text-gray-700">
              Type <code>{employee.employee_number}</code> to confirm
            </span>
            <input
              value={typedNumber}
              onChange={(e) => setTypedNumber(e.target.value)}
              className="input mt-1"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-700">
              Reason (required -- recorded in audit log)
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="input mt-1"
              placeholder="GDPR erasure request -- received 2026-04-15"
            />
          </label>
        </div>

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit || submitting}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {submitting ? "Deleting..." : "Delete Permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PersistentHeader({
  employee,
  sites,
  departments,
  onRefresh,
}: {
  employee: FormState;
  sites: Site[];
  departments: Department[];
  onRefresh: () => void;
}) {
  const [efSearchParams, setEfSearchParams] = useSearchParams();
  const [actionsOpen, setActionsOpen] = useState(false);
  const [actionModal, setActionModal] = useState<
    null | "promote" | "transfer" | "compensation" | "end_employment"
  >(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [transferSiteId, setTransferSiteId] = useState<number | "">(employee.site_id ?? "");
  const [transferDeptId, setTransferDeptId] = useState<number | "">(employee.department_id ?? "");
  const [transferDepts, setTransferDepts] = useState<Department[]>([]);
  const [actionReason, setActionReason] = useState("");
  const navigate = useNavigate();

  const first = employee.first_name ?? "";
  const last = employee.last_name ?? "";
  const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "?";
  const site = sites.find((s) => s.id === employee.site_id);
  const dept = departments.find((d) => d.id === employee.department_id);
  const meta = [
    employee.employee_number,
    site?.name,
    dept?.name,
  ]
    .filter(Boolean)
    .join(" · ");
  const isActive = employee.is_active !== false;
  const employeeId = employee.id;

  useEffect(() => {
    if (actionModal !== "transfer" || !transferSiteId) return;
    void api
      .get<Department[]>(`/api/departments?site_id=${transferSiteId}`)
      .then(setTransferDepts)
      .catch(() => setTransferDepts([]));
  }, [actionModal, transferSiteId]);

  const openModal = (m: "promote" | "transfer" | "compensation" | "end_employment") => {
    setActionsOpen(false);
    setActionReason("");
    if (m === "transfer") {
      setTransferSiteId(employee.site_id ?? "");
      setTransferDeptId(employee.department_id ?? "");
    }
    setActionModal(m);
  };

  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const action = efSearchParams.get("action");
    if (action && ["promote", "transfer", "compensation", "end_employment"].includes(action)) {
      deepLinkHandled.current = true;
      openModal(action as "promote" | "transfer" | "compensation" | "end_employment");
      setEfSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("action");
        return next;
      }, { replace: true });
    }
  }, [efSearchParams, setEfSearchParams]);

  const runAction = async (url: string, body: Record<string, unknown>) => {
    if (!employeeId) return;
    setActionBusy(true);
    try {
      await api.put(`/api/employees/${employeeId}${url}`, body);
      setActionModal(null);
      onRefresh();
    } catch (err) {
      alert((err as Error).message || "Action failed");
    } finally {
      setActionBusy(false);
    }
  };

  const handleReactivate = async () => {
    if (!employeeId) return;
    setActionsOpen(false);
    await api.put(`/api/employees/${employeeId}/reactivate`);
    onRefresh();
  };

  const handleResetClockPin = async () => {
    if (!employeeId) return;
    setActionsOpen(false);
    if (!confirm("Reset this employee's clock PIN? You'll be shown the new PIN to share with them.")) return;
    const resp = await api.post<{ new_pin: string; email_sent: boolean; email_paused?: boolean }>(`/api/employees/${employeeId}/reset-clock-pin`);
    alert(`Clock PIN reset. New PIN: ${resp.new_pin}. ${emailStatusNote(resp)}`);
  };

  const handleResetAdminPin = async () => {
    if (!employeeId) return;
    setActionsOpen(false);
    if (!confirm("Reset this employee's admin PIN? You'll be shown the new PIN to share with them.")) return;
    const resp = await api.post<{ new_pin: string; email_sent: boolean; email_paused?: boolean }>(`/api/employees/${employeeId}/reset-admin-pin`);
    alert(`Admin PIN reset. New PIN: ${resp.new_pin}. ${emailStatusNote(resp)}`);
  };

  const handleGdprDelete = () => {
    setActionsOpen(false);
    navigate(`/admin/employees/${employeeId}`, { state: { openPurge: true } });
  };

  return (
    <>
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-pme-blue text-2xl font-semibold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-bold text-gray-900">
              {first} {last}
            </h1>
            {employee.job_title && (
              <p className="truncate text-sm text-gray-500">{employee.job_title}</p>
            )}
            {meta && (
              <p className="mt-1 truncate text-xs text-gray-500">{meta}</p>
            )}
          </div>
          <span
            className={
              isActive
                ? "rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 ring-1 ring-green-200"
                : "rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-gray-200"
            }
          >
            {isActive ? "Active" : "Terminated"}
          </span>

          {employeeId && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setActionsOpen((p) => !p)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Actions ▾
              </button>
              {actionsOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setActionsOpen(false)} />
                  <div className="absolute right-0 z-40 mt-1 w-52 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                    <button type="button" onClick={() => openModal("promote")} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">
                      Promote / Change Role
                    </button>
                    <button type="button" onClick={() => openModal("transfer")} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">
                      Transfer
                    </button>
                    <button type="button" onClick={() => openModal("compensation")} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">
                      Change Compensation
                    </button>
                    <hr className="my-1 border-gray-100" />
                    <button type="button" onClick={() => void handleResetClockPin()} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">
                      Reset Clock PIN
                    </button>
                    {employee.is_admin && (
                      <button type="button" onClick={() => void handleResetAdminPin()} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">
                        Reset Admin PIN
                      </button>
                    )}
                    {isActive ? (
                      <button type="button" onClick={() => openModal("end_employment")} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                        End Employment
                      </button>
                    ) : (
                      <button type="button" onClick={() => void handleReactivate()} className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50">
                        Reactivate
                      </button>
                    )}
                    <button type="button" onClick={handleGdprDelete} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                      GDPR Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {actionModal === "promote" && (
        <PromoteModal
          employee={employee}
          sites={sites}
          busy={actionBusy}
          onCancel={() => setActionModal(null)}
          onSubmit={(body) => void runAction("/promote", body)}
        />
      )}

      {actionModal === "transfer" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold">Transfer Employee</h2>
            <label className="mt-4 block text-sm font-medium text-gray-700">New Site</label>
            <select
              value={transferSiteId}
              onChange={(e) => { setTransferSiteId(Number(e.target.value)); setTransferDeptId(""); }}
              className="input mt-1"
            >
              <option value="">Select site...</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <label className="mt-3 block text-sm font-medium text-gray-700">Department</label>
            <select
              value={transferDeptId}
              onChange={(e) => setTransferDeptId(e.target.value ? Number(e.target.value) : "")}
              className="input mt-1"
            >
              <option value="">None</option>
              {transferDepts.map((d) => (
                <option key={d.id} value={d.id}>{d.name}{d.site_name ? ` (${d.site_name})` : ""}</option>
              ))}
            </select>
            <label className="mt-3 block text-sm font-medium text-gray-700">Reason (optional)</label>
            <input
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              className="input mt-1"
              placeholder="e.g. Relocated to new branch"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setActionModal(null)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">Cancel</button>
              <button
                type="button"
                disabled={actionBusy || !transferSiteId}
                onClick={() => void runAction("/transfer", { site_id: transferSiteId, department_id: transferDeptId || null, reason: actionReason || null })}
                className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {actionBusy ? "Saving..." : "Transfer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {actionModal === "compensation" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold">Change Compensation</h2>
            <p className="mt-1 text-sm text-gray-500">
              Update pay details. Only changed fields will be saved.
            </p>
            <CompensationActionFields
              employee={employee}
              reason={actionReason}
              onReasonChange={setActionReason}
              busy={actionBusy}
              onCancel={() => setActionModal(null)}
              onSubmit={(body) => void runAction("/change-compensation", body)}
            />
          </div>
        </div>
      )}

      {actionModal === "end_employment" && (
        <EndEmploymentModal
          busy={actionBusy}
          onCancel={() => setActionModal(null)}
          onSubmit={async (body) => {
            if (!employeeId) return;
            setActionBusy(true);
            try {
              await api.put(`/api/employees/${employeeId}/deactivate`, body);
              setActionModal(null);
              onRefresh();
            } catch (err) {
              alert((err as Error).message || "Action failed");
            } finally {
              setActionBusy(false);
            }
          }}
        />
      )}
    </>
  );
}

function CompensationActionFields({
  employee,
  reason,
  onReasonChange,
  busy,
  onCancel,
  onSubmit,
}: {
  employee: FormState;
  reason: string;
  onReasonChange: (v: string) => void;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [payType, setPayType] = useState(employee.pay_type ?? "");
  const [payRate, setPayRate] = useState(employee.pay_rate?.toString() ?? "");
  const [payFreq, setPayFreq] = useState(employee.pay_frequency ?? "");
  const [paySchedule, setPaySchedule] = useState(employee.pay_schedule ?? "");

  const handleSubmit = () => {
    const body: Record<string, unknown> = { reason: reason || null };
    if (payType && payType !== employee.pay_type) body.pay_type = payType;
    if (payRate && payRate !== employee.pay_rate?.toString()) body.pay_rate = payRate;
    if (payFreq && payFreq !== employee.pay_frequency) body.pay_frequency = payFreq;
    if (paySchedule && paySchedule !== employee.pay_schedule) body.pay_schedule = paySchedule;
    if (Object.keys(body).length <= 1) return;
    onSubmit(body);
  };

  return (
    <>
      <label className="mt-4 block text-sm font-medium text-gray-700">Pay Type</label>
      <DropdownSelect listCode="pay_type" value={payType} onChange={setPayType} placeholder="—" className="input mt-1" />
      <label className="mt-3 block text-sm font-medium text-gray-700">Rate</label>
      <input value={payRate} onChange={(e) => setPayRate(e.target.value)} className="input mt-1" type="number" step="0.01" />
      <label className="mt-3 block text-sm font-medium text-gray-700">Frequency</label>
      <DropdownSelect listCode="pay_frequency" value={payFreq} onChange={setPayFreq} placeholder="—" className="input mt-1" />
      <label className="mt-3 block text-sm font-medium text-gray-700">Schedule</label>
      <DropdownSelect listCode="pay_schedule" value={paySchedule} onChange={setPaySchedule} placeholder="—" className="input mt-1" />
      <label className="mt-3 block text-sm font-medium text-gray-700">Reason</label>
      <DropdownSelect listCode="compensation_change_reason" value={reason} onChange={onReasonChange} placeholder="-- Select reason --" className="input mt-1" />
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">Cancel</button>
        <button
          type="button"
          disabled={busy}
          onClick={handleSubmit}
          className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {busy ? "Saving..." : "Update"}
        </button>
      </div>
    </>
  );
}

function PromoteModal({
  employee,
  sites,
  busy,
  onCancel,
  onSubmit,
}: {
  employee: FormState;
  sites: Site[];
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [jobTitle, setJobTitle] = useState(employee.job_title ?? "");
  const [siteId, setSiteId] = useState<number | "">(employee.site_id ?? "");
  const [deptId, setDeptId] = useState<number | "">(employee.department_id ?? "");
  const [departments, setDepartments] = useState<Department[]>([]);
  const [managerId, setManagerId] = useState<number | "">(employee.manager_id ?? "");
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [payRate, setPayRate] = useState(employee.pay_rate?.toString() ?? "");
  const [payFreq, setPayFreq] = useState(employee.pay_frequency ?? "");
  const [payCurrency, setPayCurrency] = useState(employee.pay_currency ?? "GBP");

  useEffect(() => {
    if (!siteId) return;
    void api.get<Department[]>(`/api/departments?site_id=${siteId}`).then(setDepartments).catch(() => setDepartments([]));
    const newSite = sites.find((s) => s.id === siteId);
    if (newSite && siteId !== employee.site_id) {
      setPayCurrency(REGION_ID_CURRENCY[newSite.region_id] ?? "GBP");
    }
  }, [siteId, sites, employee.site_id]);

  const handleSubmit = () => {
    const body: Record<string, unknown> = {
      reason: reason || null,
      notes: notes || null,
      effective_date: effectiveDate || null,
    };
    if (jobTitle !== (employee.job_title ?? "")) body.job_title = jobTitle;
    if (siteId && siteId !== employee.site_id) body.site_id = siteId;
    if (deptId !== (employee.department_id ?? "")) body.department_id = deptId || null;
    if (managerId !== (employee.manager_id ?? "")) body.manager_id = managerId || null;
    if (payRate) body.pay_rate = payRate;
    if (payFreq) body.pay_frequency = payFreq;
    if (payCurrency) body.pay_currency = payCurrency;
    onSubmit(body);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl max-h-[85vh] overflow-y-auto">
        <h2 className="text-lg font-semibold">Promote / Change Role</h2>
        <p className="mt-1 text-sm text-gray-500">
          Current: {employee.job_title || "No title"} · {employee.employee_number}
        </p>
        <div className="mt-4 space-y-3">
          <label className="block text-sm font-medium text-gray-700">Job Title</label>
          <ComboboxSelect
            listCode="job_title"
            value={jobTitle}
            onChange={setJobTitle}
            placeholder="e.g. Senior Baker, Production Manager"
          />

          <label className="block text-sm font-medium text-gray-700">Site</label>
          <select value={siteId} onChange={(e) => { setSiteId(Number(e.target.value)); setDeptId(""); }} className="input">
            {sites.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
          </select>

          <label className="block text-sm font-medium text-gray-700">Department</label>
          <select value={deptId} onChange={(e) => setDeptId(e.target.value ? Number(e.target.value) : "")} className="input">
            <option value="">-- None --</option>
            {departments.map((d) => (<option key={d.id} value={d.id}>{d.name}{d.site_name ? ` (${d.site_name})` : ""}</option>))}
          </select>

          <label className="block text-sm font-medium text-gray-700">Reports To</label>
          <EmployeeSearch
            value={managerId ? Number(managerId) : null}
            onChange={(v) => setManagerId(v ?? "")}
            placeholder="Search managers..."
            emptyLabel="-- No line manager --"
            excludeIds={employee.id ? [employee.id] : []}
          />

          <label className="block text-sm font-medium text-gray-700">New Pay Rate</label>
          <div className="flex items-center gap-1">
            <input type="text" inputMode="decimal" value={payRate} onChange={(e) => setPayRate(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.00" className="input w-28" />
            <DropdownSelect listCode="currency" value={payCurrency} onChange={setPayCurrency} className="input w-20" />
            <span className="text-xs text-gray-400">per</span>
            <DropdownSelect listCode="pay_frequency" value={payFreq} onChange={setPayFreq} placeholder="—" className="input w-24" />
          </div>

          <label className="block text-sm font-medium text-gray-700">Effective Date</label>
          <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} className="input" />

          <label className="block text-sm font-medium text-gray-700">Reason</label>
          <DropdownSelect listCode="compensation_change_reason" value={reason} onChange={setReason} placeholder="-- Select reason --" className="input" />

          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="input" placeholder="Additional details..." />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">Cancel</button>
          <button type="button" disabled={busy} onClick={handleSubmit} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {busy ? "Saving..." : "Confirm Promotion"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EndEmploymentModal({
  busy,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [postAccessDays, setPostAccessDays] = useState("");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
        <h2 className="text-lg font-semibold text-red-700">End Employment</h2>
        <p className="mt-1 text-sm text-gray-500">This will deactivate the employee and prevent them from signing in.</p>
        <label className="mt-4 block text-sm font-medium text-gray-700">Last Day</label>
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input mt-1" />
        <label className="mt-3 block text-sm font-medium text-gray-700">Reason</label>
        <DropdownSelect listCode="termination_reason" value={reason} onChange={setReason} placeholder="-- Select reason --" className="input mt-1" />
        <label className="mt-3 block text-sm font-medium text-gray-700">Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="input mt-1" placeholder="Additional details..." />
        <label className="mt-3 block text-sm font-medium text-gray-700">Post-Employment Access (days)</label>
        <input type="number" min={0} max={90} value={postAccessDays} onChange={(e) => setPostAccessDays(e.target.value)} className="input mt-1" placeholder="e.g. 30 (leave blank for none)" />
        <p className="mt-1 text-xs text-gray-400">Limited read-only access to download payslips and documents after termination.</p>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">Cancel</button>
          <button type="button" disabled={busy} onClick={() => onSubmit({ end_date: endDate || null, reason: reason || null, notes: notes || null, post_access_days: postAccessDays ? Number(postAccessDays) : null })} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {busy ? "Processing..." : "End Employment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function calculateTenure(startDate: string | undefined | null): string {
  if (!startDate) return "";
  const start = new Date(startDate);
  if (isNaN(start.getTime())) return "";
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  let days = now.getDate() - start.getDate();
  if (days < 0) {
    months -= 1;
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += prevMonth.getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return "";
  if (years >= 1) return `${years}y · ${months}m · ${days}d`;
  return `${months}m · ${days}d`;
}

function VitalsSidebar({
  employee,
  sites,
  departments,
  directReports,
}: {
  employee: FormState;
  sites: Site[];
  departments: Department[];
  directReports: EmployeeSummary[];
}) {
  const site = sites.find((s) => s.id === employee.site_id);
  const dept = departments.find((d) => d.id === employee.department_id);
  const employmentLabel = employee.employment_type ? formatEmploymentType(employee.employment_type) : "";
  const startDateFmt = employee.start_date
    ? new Date(employee.start_date).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;
  const tenure = calculateTenure(employee.start_date);
  return (
    <div className="sticky top-4 rounded-xl border border-gray-200 bg-white p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Vitals
      </h3>
      <ul className="mt-2 space-y-1.5 text-sm text-gray-700">
        {employee.email && (
          <li className="flex items-start gap-1.5">
            <Mail size={15} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-pme-blue" />
            <span className="break-all">{employee.email}</span>
          </li>
        )}
        {employee.phone && (
          <li className="flex items-start gap-1.5">
            <Smartphone size={15} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-pme-blue" />
            <span>{employee.phone}</span>
          </li>
        )}
        {site && <li>{site.name}</li>}
        {dept && <li>{dept.name}</li>}
        {employmentLabel && <li>{employmentLabel}</li>}
      </ul>

      <div className="my-3 border-t border-gray-100" />
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Hire Date
      </h3>
      <p className="mt-1 text-sm text-gray-700">{startDateFmt ?? "—"}</p>
      {tenure && <p className="text-xs text-gray-500">{tenure}</p>}

      <div className="my-3 border-t border-gray-100" />
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Manager
      </h3>
      {employee.manager_id && employee.manager_name ? (
        <Link
          to={`/admin/employees/${employee.manager_id}`}
          className="mt-1 block text-sm text-pme-blue hover:underline"
        >
          {employee.manager_name}
        </Link>
      ) : (
        <p className="mt-1 text-sm text-gray-400">No manager assigned</p>
      )}

      <div className="my-3 border-t border-gray-100" />
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Direct Reports
      </h3>
      {directReports.length > 0 ? (
        <ul className="mt-1 space-y-1">
          {directReports.map((r) => (
            <li key={r.id}>
              <Link
                to={`/admin/employees/${r.id}`}
                className="text-sm text-pme-blue hover:underline"
              >
                {r.first_name} {r.last_name}
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-sm text-gray-400">No direct reports</p>
      )}
      <Link
        to="/admin/org-chart"
        className="mt-2 inline-block text-xs text-pme-blue hover:underline"
      >
        View in org chart
      </Link>
    </div>
  );
}

type TabId = "profile" | "documents" | "timeoff" | "training" | "performance";

function TabBar({
  activeTab,
  onChange,
}: {
  activeTab: TabId;
  onChange: (id: TabId) => void;
}) {
  const tabs: { id: TabId; label: string }[] = [
    { id: "profile", label: "Profile" },
    { id: "documents", label: "Documents" },
    { id: "timeoff", label: "Time Off" },
    { id: "training", label: "Training & Compliance" },
    { id: "performance", label: "Performance & Activity" },
  ];
  return (
    <nav className="flex flex-wrap gap-1 border-b border-gray-200">
      {tabs.map((t) => {
        const active = t.id === activeTab;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={
              active
                ? "border-b-2 border-pme-blue px-4 py-2 text-sm font-semibold text-pme-blue"
                : "border-b-2 border-transparent px-4 py-2 text-sm text-gray-500 hover:text-gray-700"
            }
          >
            {t.label}
          </button>
        );
      })}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Documents tab — placeholder doc vault + education / visa / license / work
// ---------------------------------------------------------------------------

function DocumentsTab({ employeeId }: { employeeId: number | undefined }) {
  if (!employeeId) {
    return (
      <p className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500">
        Save the employee before adding documents.
      </p>
    );
  }
  return (
    <div className="space-y-4">
      <JobDescriptionCard employeeId={employeeId} />
      <EmployeeDocumentsSection employeeId={employeeId} />
      <EducationSection employeeId={employeeId} />
      <VisaSection employeeId={employeeId} />
      <LicenseSection employeeId={employeeId} />
      <WorkHistorySection employeeId={employeeId} />
    </div>
  );
}

/** The job description kept in PME People: where it stands, and the way in
 *  to edit it (instead of uploading a new file). */
function JobDescriptionCard({ employeeId }: { employeeId: number }) {
  const [info, setInfo] = useState<{ status: string; version: number | null; date: string | null; draft: boolean } | null>(null);
  useEffect(() => {
    let live = true;
    api.get<{ current: { status: string; version: number; issued_at: string | null; signed_at: string | null } | null; draft: unknown }>(
      `/api/job-descriptions/employee/${employeeId}`,
    )
      .then((p) => {
        if (!live) return;
        const c = p.current;
        setInfo({ status: c?.status ?? "none", version: c?.version ?? null, date: c?.signed_at ?? c?.issued_at ?? null, draft: !!p.draft });
      })
      .catch(() => live && setInfo(null));
    return () => { live = false; };
  }, [employeeId]);
  if (!info) return null;
  const when = info.date ? new Date(info.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";
  const text: Record<string, string> = {
    none: "Not in PME People yet. Write it there, or read it in from an uploaded file.",
    issued: `Version ${info.version} sent ${when}, waiting for them to sign.`,
    changes_requested: `They asked for a change to version ${info.version}.`,
    signed: `Version ${info.version} signed ${when}.`,
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-pme-blue/30 bg-pme-sky/30 p-4">
      <FileSignature size={20} className="shrink-0 text-pme-blue" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-pme-navy">Job description</p>
        <p className="text-xs text-gray-700">
          {text[info.status] ?? ""}
          {info.draft ? " There is a draft not issued yet." : ""}
        </p>
      </div>
      <Link
        to={`/job-description/${employeeId}`}
        className="inline-flex min-h-[40px] items-center rounded-lg bg-pme-blue px-4 text-xs font-semibold text-white"
      >
        {info.status === "none" && !info.draft ? "Write job description" : "Open job description"}
      </Link>
    </div>
  );
}

async function uploadSupportingDoc(file: File, employeeId: number, title: string): Promise<number> {
  const params = new URLSearchParams({ title, category_id: "1", employee_id: String(employeeId) });
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/documents?${params.toString()}`, { method: "POST", credentials: "include", body: form });
  if (!res.ok) throw new Error("Document upload failed");
  const data = await res.json() as { id: number };
  return data.id;
}

interface EmpDoc { id: number; title: string; file_name: string; file_type: string; expires_at: string | null; requires_signature: boolean; signed_at: string | null; signature_mode: string | null; created_at: string; category_id: number; can_remove?: boolean; last_emailed_at?: string | null; }

const EMP_DOC_VIEWABLE = new Set(["pdf", "jpg", "jpeg", "png", "gif"]);

function EmployeeDocumentsSection({ employeeId }: { employeeId: number }) {
  const [docs, setDocs] = useState<EmpDoc[]>([]);
  const [signingDoc, setSigningDoc] = useState<EmpDoc | null>(null);
  const [verifyDocId, setVerifyDocId] = useState<number | null>(null);
  const [previewDoc, setPreviewDoc] = useState<EmpDoc | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);

  const load = useCallback(async () => {
    try {
      const [d, c] = await Promise.all([
        api.get<EmpDoc[]>(`/api/documents?employee_id=${employeeId}`),
        api.get<{ id: number; name: string }[]>("/api/documents/categories"),
      ]);
      setDocs(d);
      setCategories(c);
    } catch {
      setDocs([]);
    }
  }, [employeeId]);

  useEffect(() => { void load(); }, [load]);

  const openDoc = async (docId: number, disposition: "inline" | "attachment") => {
    try {
      const resp = await fetch(`/api/documents/${docId}/download?disposition=${disposition}`, { credentials: "include" });
      if (!resp.ok) return;
      const ct = resp.headers.get("content-type") ?? "";
      if (ct.includes("application/json")) {
        const data = await resp.json() as { url: string };
        window.open(data.url, "_blank");
      } else {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        if (disposition === "inline") {
          window.open(url, "_blank");
        } else {
          const a = document.createElement("a");
          a.href = url; a.download = ""; a.click();
          URL.revokeObjectURL(url);
        }
      }
    } catch { /* ignore */ }
  };

  return (
    <Section title="Documents">
      <div className="mb-3">
        <button
          type="button"
          onClick={() => setShowUpload(true)}
          className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white"
        >
          + Upload Document
        </button>
      </div>

      {showUpload && (
        <EmpDocUploadForm
          employeeId={employeeId}
          categories={categories}
          onDone={() => { setShowUpload(false); void load(); }}
          onCancel={() => setShowUpload(false)}
        />
      )}

      {docs.length === 0 ? (
        <p className="text-sm text-gray-400">No documents for this employee.</p>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-start justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">{d.title}</p>
                <p className="text-xs text-gray-500">{d.file_name} · {d.file_type.toUpperCase()}</p>
                {d.expires_at && <p className="text-[11px] text-amber-600">Expires: {d.expires_at}</p>}
                {d.requires_signature && !d.signed_at && <span className="mt-0.5 inline-block rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">Signature Required</span>}
                {d.signed_at && <span className="mt-0.5 inline-block rounded bg-green-50 px-1.5 py-0.5 text-[10px] font-semibold text-green-700">Signed</span>}
              </div>
              <div className="flex shrink-0 flex-wrap gap-1">
                {EMP_DOC_VIEWABLE.has(d.file_type.toLowerCase()) && (
                  <button type="button" onClick={() => setPreviewDoc(d)} className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50">View</button>
                )}
                <button type="button" onClick={() => void openDoc(d.id, "attachment")} className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50">Download</button>
                {d.requires_signature && !d.signed_at && (
                  <button type="button" onClick={() => setSigningDoc(d)} className="rounded-lg bg-amber-500 px-2 py-1 text-xs font-semibold text-white">Sign</button>
                )}
                {d.signed_at && (
                  <button type="button" onClick={() => setVerifyDocId(d.id)} className="rounded-lg border border-green-200 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50">Verify Signature</button>
                )}
                <EmailDocumentButton
                  compact
                  documentId={d.id}
                  needsSignature={d.requires_signature && !d.signed_at}
                  lastEmailedAt={d.last_emailed_at}
                />
                {d.can_remove && (
                  <RemoveDocumentButton compact documentId={d.id} title={d.title} onRemoved={() => void load()} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {signingDoc && (
        <SignatureModal
          doc={signingDoc}
          onClose={(signed) => {
            setSigningDoc(null);
            if (signed) void load();
          }}
        />
      )}
      {verifyDocId !== null && (
        <SignatureDetailsModal
          documentId={verifyDocId}
          onClose={() => setVerifyDocId(null)}
        />
      )}
      {previewDoc && (
        <DocumentPreviewModal
          documentId={previewDoc.id}
          title={previewDoc.title}
          fileType={previewDoc.file_type}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </Section>
  );
}

function EmpDocUploadForm({ employeeId, categories, onDone, onCancel }: { employeeId: number; categories: { id: number; name: string }[]; onDone: () => void; onCancel: () => void }) {
  const [categoryId, setCategoryId] = useState<string>(categories[0]?.id?.toString() ?? "");
  const [title, setTitle] = useState("");
  const [requiresSig, setRequiresSig] = useState(false);
  const [requiresInitials, setRequiresInitials] = useState(false);
  const [signatureMode, setSignatureMode] = useState("confirm_receipt");
  const [notify, setNotify] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError("Select a file"); return; }
    setSubmitting(true); setError(null);
    try {
      const params = new URLSearchParams({
        title, category_id: categoryId, employee_id: String(employeeId),
        requires_signature: String(requiresSig),
        requires_initials: String(requiresInitials),
        notify: String(notify),
      });
      if (requiresSig) params.set("signature_mode", signatureMode);
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`/api/documents?${params.toString()}`, {
        method: "POST", credentials: "include", body: form,
      });
      if (!res.ok) { const b = await res.json().catch(() => null); throw new Error(b?.detail ?? "Upload failed"); }
      onDone();
    } catch (err) { setError((err as Error).message); } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="mb-3 space-y-3 rounded-lg border border-gray-200 bg-white p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-xs">
          <span className="font-medium text-gray-700">Category</span>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="input mt-1">
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="block text-xs">
          <span className="font-medium text-gray-700">Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Employment Contract" className="input mt-1" />
        </label>
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={requiresSig} onChange={(e) => setRequiresSig(e.target.checked)} />
          Requires e-signature
        </label>
        {requiresSig && signatureMode === "on_document" && (
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={requiresInitials} onChange={(e) => setRequiresInitials(e.target.checked)} />
            Requires initials on each page
          </label>
        )}
      </div>
      {requiresSig && (
        <label className="block text-xs">
          <span className="font-medium text-gray-700">Signature Mode</span>
          <select value={signatureMode} onChange={(e) => setSignatureMode(e.target.value)} className="input mt-1">
            <option value="confirm_receipt">Confirm Receipt</option>
            <option value="on_document">On Document</option>
          </select>
        </label>
      )}
      <label className="block text-xs">
        <span className="font-medium text-gray-700">File</span>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={(e) => setFile(e.target.files?.[0] ?? null)} required className="mt-1 block w-full text-xs file:mr-3 file:rounded-lg file:border-0 file:bg-pme-blue file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white" />
      </label>
      <label className="flex items-start gap-2 text-xs">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="mt-0.5" />
        <span>
          Email the employee now
          <span className="block text-gray-500">Untick to upload quietly and send later with "Email for signature" on the document.</span>
        </span>
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">{submitting ? "Uploading..." : "Upload"}</button>
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs">Cancel</button>
      </div>
    </form>
  );
}

function EntitySection({
  title,
  adding,
  onAdd,
  onCancel,
  children,
  form,
}: {
  title: string;
  adding: boolean;
  onAdd: () => void;
  onCancel: () => void;
  children: ReactNode;
  form?: ReactNode;
}) {
  return (
    <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {title}
        </h2>
        {!adding && (
          <button
            type="button"
            onClick={onAdd}
            className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white"
          >
            + Add
          </button>
        )}
        {adding && (
          <button
            type="button"
            onClick={onCancel}
            className="text-xs text-gray-500"
          >
            Cancel
          </button>
        )}
      </div>
      {adding && form}
      {children}
    </section>
  );
}

function EntityRow({
  primary,
  secondary,
  meta,
  onEdit,
  onDelete,
}: {
  primary: ReactNode;
  secondary?: ReactNode;
  meta?: ReactNode;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">{primary}</p>
        {secondary && (
          <p className="mt-0.5 text-xs text-gray-600">{secondary}</p>
        )}
        {meta && <p className="mt-0.5 text-[11px] text-gray-400">{meta}</p>}
      </div>
      <div className="flex shrink-0 gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-600"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function fmtDate(d: string | null): string {
  if (!d) return "";
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function dateRange(start: string | null, end: string | null): string {
  if (!start && !end) return "";
  return `${fmtDate(start) || "?"} – ${fmtDate(end) || "Present"}`;
}

// --- Education --------------------------------------------------------------

function EducationSection({ employeeId }: { employeeId: number }) {
  const [items, setItems] = useState<Education[]>([]);
  const [editing, setEditing] = useState<Education | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await api.get<Education[]>(
        `/api/employees/${employeeId}/education`,
      );
      setItems(list);
    } catch {
      setItems([]);
    }
  }, [employeeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (id: number) => {
    if (!confirm("Delete this education record?")) return;
    await api.delete(`/api/education/${id}`);
    await load();
  };

  const formNode = (
    <EducationForm
      employeeId={employeeId}
      initial={editing ?? undefined}
      onSaved={async () => {
        setAdding(false);
        setEditing(null);
        await load();
      }}
      onCancel={() => {
        setAdding(false);
        setEditing(null);
      }}
    />
  );

  return (
    <EntitySection
      title="Education"
      adding={adding || editing !== null}
      onAdd={() => {
        setAdding(true);
        setEditing(null);
      }}
      onCancel={() => {
        setAdding(false);
        setEditing(null);
      }}
      form={formNode}
    >
      {items.length === 0 ? (
        <p className="text-xs text-gray-400">No education recorded.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((e) => (
            <li key={e.id}>
              <EntityRow
                primary={
                  <>
                    {e.institution}
                    {e.degree ? ` — ${e.degree}` : ""}
                  </>
                }
                secondary={e.major_specialisation ?? undefined}
                meta={
                  <>
                    {dateRange(e.start_date, e.end_date)}
                    {e.gpa ? ` · Grade ${e.gpa}` : ""}
                  </>
                }
                onEdit={() => {
                  setEditing(e);
                  setAdding(false);
                }}
                onDelete={() => void remove(e.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </EntitySection>
  );
}

function EducationForm({
  employeeId,
  initial,
  onSaved,
  onCancel,
}: {
  employeeId: number;
  initial?: Education;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const [institution, setInstitution] = useState(initial?.institution ?? "");
  const [degree, setDegree] = useState(initial?.degree ?? "");
  const [majorSpecialisation, setMajorSpecialisation] = useState(initial?.major_specialisation ?? "");
  const [startDate, setStartDate] = useState(initial?.start_date ?? "");
  const [endDate, setEndDate] = useState(initial?.end_date ?? "");
  const [gpa, setGpa] = useState(initial?.gpa ?? "");
  const [docFile, setDocFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!institution.trim()) return;
    setSaving(true);
    try {
      let documentId: number | null = initial?.document_id ?? null;

      if (docFile) {
        documentId = await uploadSupportingDoc(docFile, employeeId, `Education: ${institution.trim()}`);
      }

      const body = {
        institution: institution.trim(),
        degree: degree || null,
        major_specialisation: majorSpecialisation || null,
        start_date: startDate || null,
        end_date: endDate || null,
        gpa: gpa || null,
        document_id: documentId,
      };
      if (initial) {
        await api.put(`/api/education/${initial.id}`, body);
      } else {
        await api.post(`/api/employees/${employeeId}/education`, body);
      }
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/40 p-3">
      <Row>
        <Field label="Institution">
          <input
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
            className="input"
            required
          />
        </Field>
        <Field label="Degree">
          <input
            value={degree}
            onChange={(e) => setDegree(e.target.value)}
            className="input"
            placeholder="e.g. BSc, MBA"
          />
        </Field>
      </Row>
      <Field label="Field of study">
        <input
          value={majorSpecialisation}
          onChange={(e) => setMajorSpecialisation(e.target.value)}
          className="input"
        />
      </Field>
      <Row>
        <Field label="Start date">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="End date">
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="input"
          />
        </Field>
      </Row>
      <Field label="GPA">
        <input
          value={gpa}
          onChange={(e) => setGpa(e.target.value)}
          className="input max-w-xs"
        />
      </Field>
      <Field label="Supporting document">
        {initial?.document_id && !docFile && <p className="mb-1 text-xs text-green-600">Document attached (ID: {initial.document_id})</p>}
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.docx"
          onChange={(e) => setDocFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-xs file:font-medium"
        />
      </Field>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || !institution.trim()}
          className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        >
          {saving ? "Saving..." : initial ? "Update" : "Add"}
        </button>
      </div>
    </div>
  );
}

// --- Visa / Right to Work ---------------------------------------------------


function VisaSection({ employeeId }: { employeeId: number }) {
  const [items, setItems] = useState<VisaRightToWork[]>([]);
  const [editing, setEditing] = useState<VisaRightToWork | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await api.get<VisaRightToWork[]>(
        `/api/employees/${employeeId}/visa`,
      );
      setItems(list);
    } catch {
      setItems([]);
    }
  }, [employeeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (id: number) => {
    if (!confirm("Delete this visa record?")) return;
    await api.delete(`/api/visa/${id}`);
    await load();
  };

  const formNode = (
    <VisaForm
      employeeId={employeeId}
      initial={editing ?? undefined}
      onSaved={async () => {
        setAdding(false);
        setEditing(null);
        await load();
      }}
      onCancel={() => {
        setAdding(false);
        setEditing(null);
      }}
    />
  );

  return (
    <EntitySection
      title="Visa / Right to Work"
      adding={adding || editing !== null}
      onAdd={() => {
        setAdding(true);
        setEditing(null);
      }}
      onCancel={() => {
        setAdding(false);
        setEditing(null);
      }}
      form={formNode}
    >
      {items.length === 0 ? (
        <p className="text-xs text-gray-400">No visa records.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((v) => (
            <li key={v.id}>
              <EntityRow
                primary={
                  <>
                    {v.visa_type ?? "Visa"}{" "}
                    <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700">
                      {v.status}
                    </span>
                  </>
                }
                secondary={
                  [v.issuing_country, v.reference_number]
                    .filter(Boolean)
                    .join(" · ") || undefined
                }
                meta={dateRange(v.issued_date, v.expiry_date)}
                onEdit={() => {
                  setEditing(v);
                  setAdding(false);
                }}
                onDelete={() => void remove(v.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </EntitySection>
  );
}

function VisaForm({
  employeeId,
  initial,
  onSaved,
  onCancel,
}: {
  employeeId: number;
  initial?: VisaRightToWork;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const [status, setStatus] = useState<VisaStatus>(initial?.status ?? "active");
  const [visaType, setVisaType] = useState(initial?.visa_type ?? "");
  const [referenceNumber, setReferenceNumber] = useState(initial?.reference_number ?? "");
  const [issuingCountry, setIssuingCountry] = useState(
    initial?.issuing_country ?? "",
  );
  const [issuedDate, setIssuedDate] = useState(initial?.issued_date ?? "");
  const [expiryDate, setExpiryDate] = useState(initial?.expiry_date ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [docFile, setDocFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      let documentId: number | null = initial?.document_id ?? null;
      if (docFile) {
        documentId = await uploadSupportingDoc(docFile, employeeId, `Visa: ${visaType || "Document"}`);
      }
      const body = {
        status,
        visa_type: visaType,
        issuing_country: issuingCountry,
        issued_date: issuedDate || null,
        expiry_date: expiryDate || null,
        reference_number: referenceNumber || null,
        notes: notes || null,
        document_id: documentId,
      };
      if (initial) {
        await api.put(`/api/visa/${initial.id}`, body);
      } else {
        await api.post(`/api/employees/${employeeId}/visa`, body);
      }
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/40 p-3">
      <Row>
        <Field label="Visa type">
          <DropdownSelect
            listCode="visa_type"
            value={visaType}
            onChange={setVisaType}
            placeholder="-- Select --"
          />
        </Field>
        <Field label="Status">
          <DropdownSelect
            listCode="visa_status"
            value={status}
            onChange={(v) => setStatus(v as VisaStatus)}
          />
        </Field>
      </Row>
      <Row>
        <Field label="Issuing country">
          <input
            value={issuingCountry}
            onChange={(e) => setIssuingCountry(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Reference number">
          <input
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            className="input"
          />
        </Field>
      </Row>
      <Row>
        <Field label="Issued date">
          <input
            type="date"
            value={issuedDate}
            onChange={(e) => setIssuedDate(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Expiry date">
          <input
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            className="input"
          />
        </Field>
      </Row>
      <Field label="Notes">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="input"
        />
      </Field>
      <Field label="Supporting document">
        {initial?.document_id && !docFile && <p className="mb-1 text-xs text-green-600">Document attached (ID: {initial.document_id})</p>}
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.docx"
          onChange={(e) => setDocFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-xs file:font-medium"
        />
      </Field>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        >
          {saving ? "Saving..." : initial ? "Update" : "Add"}
        </button>
      </div>
    </div>
  );
}

// --- Drivers License --------------------------------------------------------

function LicenseSection({ employeeId }: { employeeId: number }) {
  const [items, setItems] = useState<DriversLicense[]>([]);
  const [editing, setEditing] = useState<DriversLicense | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await api.get<DriversLicense[]>(
        `/api/employees/${employeeId}/drivers-license`,
      );
      setItems(list);
    } catch {
      setItems([]);
    }
  }, [employeeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (id: number) => {
    if (!confirm("Delete this licence record?")) return;
    await api.delete(`/api/drivers-license/${id}`);
    await load();
  };

  const formNode = (
    <LicenseForm
      employeeId={employeeId}
      initial={editing ?? undefined}
      onSaved={async () => {
        setAdding(false);
        setEditing(null);
        await load();
      }}
      onCancel={() => {
        setAdding(false);
        setEditing(null);
      }}
    />
  );

  return (
    <EntitySection
      title="Drivers Licence"
      adding={adding || editing !== null}
      onAdd={() => {
        setAdding(true);
        setEditing(null);
      }}
      onCancel={() => {
        setAdding(false);
        setEditing(null);
      }}
      form={formNode}
    >
      {items.length === 0 ? (
        <p className="text-xs text-gray-400">No licences recorded.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((l) => (
            <li key={l.id}>
              <EntityRow
                primary={
                  <>
                    {l.license_number}
                    {l.license_code ? ` (Class ${l.license_code})` : ""}
                  </>
                }
                secondary={l.issuing_country ?? undefined}
                meta={dateRange(l.issue_date, l.expiry_date)}
                onEdit={() => {
                  setEditing(l);
                  setAdding(false);
                }}
                onDelete={() => void remove(l.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </EntitySection>
  );
}

function LicenseForm({
  employeeId,
  initial,
  onSaved,
  onCancel,
}: {
  employeeId: number;
  initial?: DriversLicense;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const [licenseNumber, setLicenseNumber] = useState(
    initial?.license_number ?? "",
  );
  const [licenseCode, setLicenseCode] = useState(initial?.license_code ?? "");
  const [issuingCountry, setIssuingCountry] = useState(
    initial?.issuing_country ?? "",
  );
  const [issueDate, setIssueDate] = useState(initial?.issue_date ?? "");
  const [expiryDate, setExpiryDate] = useState(initial?.expiry_date ?? "");
  const [vehicleRegistration, setVehicleRegistration] = useState(initial?.vehicle_registration ?? "");
  const [docFile, setDocFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!licenseNumber.trim()) return;
    setSaving(true);
    try {
      let documentId: number | null = initial?.document_id ?? null;
      if (docFile) {
        documentId = await uploadSupportingDoc(docFile, employeeId, `Licence: ${licenseNumber.trim()}`);
      }
      const body = {
        license_number: licenseNumber.trim(),
        license_code: licenseCode || null,
        issuing_country: issuingCountry || null,
        issue_date: issueDate || null,
        expiry_date: expiryDate || null,
        vehicle_registration: vehicleRegistration || null,
        document_id: documentId,
      };
      if (initial) {
        await api.put(`/api/drivers-license/${initial.id}`, body);
      } else {
        await api.post(`/api/employees/${employeeId}/drivers-license`, body);
      }
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/40 p-3">
      <Row>
        <Field label="Licence number">
          <input
            value={licenseNumber}
            onChange={(e) => setLicenseNumber(e.target.value)}
            className="input"
            required
          />
        </Field>
        <Field label="License code">
          <DropdownSelect
            listCode="drivers_license_code"
            value={licenseCode}
            onChange={setLicenseCode}
            placeholder="-- Select --"
          />
        </Field>
      </Row>
      <Field label="Issuing country">
        <input
          value={issuingCountry}
          onChange={(e) => setIssuingCountry(e.target.value)}
          className="input"
        />
      </Field>
      <Row>
        <Field label="Issue date">
          <input
            type="date"
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Expiry date">
          <input
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            className="input"
          />
        </Field>
      </Row>
      <Field label="Vehicle registration">
        <input
          value={vehicleRegistration}
          onChange={(e) => setVehicleRegistration(e.target.value)}
          className="input"
        />
      </Field>
      <Field label="Supporting document">
        {initial?.document_id && !docFile && <p className="mb-1 text-xs text-green-600">Document attached (ID: {initial.document_id})</p>}
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.docx"
          onChange={(e) => setDocFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-xs file:font-medium"
        />
      </Field>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || !licenseNumber.trim()}
          className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        >
          {saving ? "Saving..." : initial ? "Update" : "Add"}
        </button>
      </div>
    </div>
  );
}

// --- Previous Work Experience -----------------------------------------------

function WorkHistorySection({ employeeId }: { employeeId: number }) {
  const [items, setItems] = useState<PreviousWorkExperience[]>([]);
  const [editing, setEditing] = useState<PreviousWorkExperience | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await api.get<PreviousWorkExperience[]>(
        `/api/employees/${employeeId}/work-history`,
      );
      setItems(list);
    } catch {
      setItems([]);
    }
  }, [employeeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (id: number) => {
    if (!confirm("Delete this work history entry?")) return;
    await api.delete(`/api/work-history/${id}`);
    await load();
  };

  const formNode = (
    <WorkHistoryForm
      employeeId={employeeId}
      initial={editing ?? undefined}
      onSaved={async () => {
        setAdding(false);
        setEditing(null);
        await load();
      }}
      onCancel={() => {
        setAdding(false);
        setEditing(null);
      }}
    />
  );

  return (
    <EntitySection
      title="Previous Work Experience"
      adding={adding || editing !== null}
      onAdd={() => {
        setAdding(true);
        setEditing(null);
      }}
      onCancel={() => {
        setAdding(false);
        setEditing(null);
      }}
      form={formNode}
    >
      {items.length === 0 ? (
        <p className="text-xs text-gray-400">No work history recorded.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((w) => (
            <li key={w.id}>
              <EntityRow
                primary={
                  <>
                    {w.job_title ? `${w.job_title} at ` : ""}
                    {w.company}
                  </>
                }
                secondary={w.reason_for_leaving ?? undefined}
                meta={dateRange(w.start_date, w.end_date)}
                onEdit={() => {
                  setEditing(w);
                  setAdding(false);
                }}
                onDelete={() => void remove(w.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </EntitySection>
  );
}

function WorkHistoryForm({
  employeeId,
  initial,
  onSaved,
  onCancel,
}: {
  employeeId: number;
  initial?: PreviousWorkExperience;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const [company, setCompany] = useState(initial?.company ?? "");
  const [jobTitle, setJobTitle] = useState(initial?.job_title ?? "");
  const [startDate, setStartDate] = useState(initial?.start_date ?? "");
  const [endDate, setEndDate] = useState(initial?.end_date ?? "");
  const [reasonForLeaving, setReasonForLeaving] = useState(
    initial?.reason_for_leaving ?? "",
  );
  const [responsibilities, setResponsibilities] = useState(
    initial?.responsibilities ?? "",
  );
  const [docFile, setDocFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!company.trim()) return;
    setSaving(true);
    try {
      let documentId: number | null = initial?.document_id ?? null;
      if (docFile) {
        documentId = await uploadSupportingDoc(docFile, employeeId, `Work: ${company.trim()}`);
      }
      const body = {
        company: company.trim(),
        job_title: jobTitle || null,
        start_date: startDate || null,
        end_date: endDate || null,
        reason_for_leaving: reasonForLeaving || null,
        responsibilities: responsibilities || null,
        document_id: documentId,
      };
      if (initial) {
        await api.put(`/api/work-history/${initial.id}`, body);
      } else {
        await api.post(`/api/employees/${employeeId}/work-history`, body);
      }
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/40 p-3">
      <Row>
        <Field label="Company">
          <input
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className="input"
            required
          />
        </Field>
        <Field label="Job title">
          <input
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            className="input"
          />
        </Field>
      </Row>
      <Row>
        <Field label="Start date">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="End date">
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="input"
          />
        </Field>
      </Row>
      <Field label="Responsibilities">
        <textarea
          value={responsibilities}
          onChange={(e) => setResponsibilities(e.target.value)}
          rows={3}
          className="input"
        />
      </Field>
      <Field label="Reason for leaving">
        <input
          value={reasonForLeaving}
          onChange={(e) => setReasonForLeaving(e.target.value)}
          className="input"
        />
      </Field>
      <Field label="Supporting document">
        {initial?.document_id && !docFile && <p className="mb-1 text-xs text-green-600">Document attached (ID: {initial.document_id})</p>}
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.docx"
          onChange={(e) => setDocFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-xs file:font-medium"
        />
      </Field>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || !company.trim()}
          className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        >
          {saving ? "Saving..." : initial ? "Update" : "Add"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Training tab — placeholder + simple fetch
// ---------------------------------------------------------------------------

function TrainingTab({ employeeId }: { employeeId: number | undefined }) {
  const [rows, setRows] = useState<unknown[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!employeeId) return;
    void (async () => {
      try {
        const res = await api.get<unknown>(
          `/api/training/courses?employee_id=${employeeId}`,
        );
        setRows(Array.isArray(res) ? res : []);
      } catch {
        setUnavailable(true);
      }
    })();
  }, [employeeId]);

  return (
    <Section title="Training & Compliance">
      <p className="text-sm text-gray-600">
        Training and compliance for this employee — assigned courses, external
        training log, completed history, and the compliance matrix. The
        per-employee view is coming in the next iteration.
      </p>
      {!unavailable && rows && rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2">Course</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const obj = r as Record<string, unknown>;
                const name =
                  typeof obj.name === "string"
                    ? obj.name
                    : typeof obj.title === "string"
                      ? obj.title
                      : `#${i + 1}`;
                return (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-3 py-2 text-gray-800">{name}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Link
        to="/admin/training"
        className="inline-block text-sm font-medium text-pme-blue hover:underline"
      >
        Open the Training admin area →
      </Link>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Performance tab — recognition stats, assets, requisitions, activity timeline
// ---------------------------------------------------------------------------

// --- Reviews Section --------------------------------------------------------

interface EmployeeReview {
  id: number;
  cycle_name: string;
  employee_id: number;
  employee_name: string;
  manager_id?: number | null;
  manager_name: string | null;
  status: string;
  overall_self_rating?: number | null;
  overall_manager_rating?: number | null;
  overall_agreed_rating?: number | null;
  recommendation?: string | null;
  is_historical?: boolean;
  review_date?: string | null;
  document_count?: number;
}

const REVIEW_STATUS_BADGE: Record<string, string> = {
  completed: "bg-green-50 text-green-700",
  meeting_pending: "bg-amber-50 text-amber-800",
  employee_response_pending: "bg-amber-50 text-amber-800",
  not_started: "bg-gray-100 text-gray-600",
  cancelled: "bg-gray-100 text-gray-400",
};

function reviewFilterForStatus(status: string): string {
  if (status === "completed") return "completed";
  if (status === "meeting_pending" || status === "employee_response_pending") return "meeting";
  return "active";
}

function ReviewsSection({ employeeId, employeeName }: { employeeId: number; employeeName?: string }) {
  const [reviews, setReviews] = useState<EmployeeReview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void (async () => {
      try {
        const data = await api.get<EmployeeReview[]>(`/api/reviews?employee_id=${employeeId}&admin_context=true`);
        setReviews(data);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [employeeId]);

  const latestCompleted = reviews.find((r) => r.status === "completed");

  return (
    <Section title="Performance Reviews">
      {loading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : (
        <>
          {/* Latest Review Summary Card */}
          {latestCompleted ? (
            <div className="rounded-lg border border-green-200 bg-green-50/50 p-4 mb-3">
              <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Latest Completed Review</p>
              <div className="flex items-baseline gap-4">
                <p className="text-2xl font-semibold text-gray-900">
                  {latestCompleted.overall_agreed_rating != null ? `${latestCompleted.overall_agreed_rating}/5` : "–"}
                </p>
                <span className="text-xs text-gray-500">Agreed Rating</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                <span>{latestCompleted.cycle_name}</span>
                {latestCompleted.review_date && <span>{latestCompleted.review_date}</span>}
                {latestCompleted.recommendation && (
                  <span className="capitalize">{latestCompleted.recommendation.replace(/_/g, " ")}</span>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400 mb-3">No completed reviews</p>
          )}

          {/* Review list */}
          {reviews.length === 0 ? (
            <p className="text-xs text-gray-400">No reviews for this employee.</p>
          ) : (
            <div className="space-y-2">
              {reviews.map((r) => (
                <Link
                  key={r.id}
                  to={`/admin/performance?tab=360-reviews&filter=${reviewFilterForStatus(r.status)}&employee=${encodeURIComponent(employeeName || r.employee_name)}`}
                  className="block rounded-lg border border-gray-100 bg-gray-50 p-3 transition-colors hover:bg-gray-100"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {r.cycle_name}
                        {r.is_historical && (
                          <span className="ml-2 inline-block rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">Historical</span>
                        )}
                        {(r.document_count ?? 0) > 0 && (
                          <span className="ml-1.5 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">{r.document_count} doc{r.document_count !== 1 ? "s" : ""}</span>
                        )}
                      </p>
                      {r.manager_name && <p className="text-xs text-gray-500">Manager: {r.manager_name}</p>}
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${REVIEW_STATUS_BADGE[r.status] ?? "bg-blue-50 text-blue-700"}`}>
                      {reviewStatusLabel(r.status)}
                    </span>
                  </div>
                  <div className="mt-2 flex gap-3 text-xs text-gray-500">
                    <span>Self: {r.overall_self_rating != null ? `${r.overall_self_rating}/5` : "–"}</span>
                    <span>Manager: {r.overall_manager_rating != null ? `${r.overall_manager_rating}/5` : "–"}</span>
                    <span>Agreed: {r.overall_agreed_rating != null ? `${r.overall_agreed_rating}/5` : "–"}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-3">
            <Link
              to="/admin/performance?tab=360-reviews"
              className="rounded-lg border border-pme-blue px-3 py-1.5 text-xs font-semibold text-pme-blue hover:bg-pme-blue/5"
            >
              Start Review
            </Link>
          </div>
        </>
      )}
    </Section>
  );
}

function PerformanceTab({ employeeId, employeeName }: { employeeId: number | undefined; employeeName?: string }) {
  if (!employeeId) {
    return (
      <p className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500">
        Save the employee before viewing performance data.
      </p>
    );
  }
  return (
    <div className="space-y-4">
      <ReviewsSection employeeId={employeeId} employeeName={employeeName} />
      <RecognitionStatsCard employeeId={employeeId} />
      <DisciplinarySection employeeId={employeeId} />
      <AssetsSection employeeId={employeeId} />
      <RequisitionsSection employeeId={employeeId} />
      <EmployeeTimeline employeeId={employeeId} />
    </div>
  );
}

function RecognitionStatsCard({ employeeId }: { employeeId: number }) {
  const [stats, setStats] = useState<RecognitionStats | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get<RecognitionStats>(
          `/api/recognition/stats?employee_id=${employeeId}`,
        );
        setStats(res);
      } catch {
        setUnavailable(true);
      }
    })();
  }, [employeeId]);

  return (
    <Section title="Recognition">
      {unavailable ? (
        <p className="text-xs text-gray-400">No recognition data available.</p>
      ) : stats ? (
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">
              Received
            </p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">
              {stats.total_received}
            </p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">
              Given
            </p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">
              {stats.total_given}
            </p>
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-400">Loading…</p>
      )}
    </Section>
  );
}

// --- Disciplinary -----------------------------------------------------------

function DisciplinarySection({ employeeId }: { employeeId: number }) {
  const { t } = useTranslation();
  const [cases, setCases] = useState<DisciplinaryCase[]>([]);
  const [adding, setAdding] = useState(false);
  const [caseForm, setCaseForm] = useState({ disciplinary_type: "", subject: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setCases(await api.get<DisciplinaryCase[]>(`/api/disciplinary/cases?employee_id=${employeeId}`));
    } catch { setCases([]); }
  }, [employeeId]);

  useEffect(() => { void load(); }, [load]);

  const create = async () => {
    if (!caseForm.subject) return;
    setSaving(true);
    try {
      await api.post("/api/disciplinary/cases", {
        employee_id: employeeId,
        disciplinary_type: caseForm.disciplinary_type || "misconduct",
        subject: caseForm.subject,
        description: caseForm.description || null,
      });
      setCaseForm({ disciplinary_type: "", subject: "", description: "" });
      setAdding(false);
      await load();
    } finally { setSaving(false); }
  };

  const statusColor = (s: string) => {
    if (s === "closed") return "bg-gray-100 text-gray-600";
    if (s === "outcome_issued") return "bg-yellow-100 text-yellow-700";
    return "bg-red-50 text-red-700";
  };

  return (
    <Section title={t("employeeForm.disciplinary", "Disciplinary")}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase text-gray-500">{t("employeeForm.disciplinaryCases", "Cases")}</h3>
          {!adding && (
            <button type="button" onClick={() => setAdding(true)} className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white">
              + {t("employeeForm.openCase", "Open Case")}
            </button>
          )}
        </div>
        {adding && (
          <div className="rounded-lg border border-gray-200 p-3 space-y-2">
            <DropdownSelect listCode="disciplinary_type" value={caseForm.disciplinary_type} onChange={(v) => setCaseForm({ ...caseForm, disciplinary_type: v })} placeholder={t("employeeForm.disciplinaryType", "Disciplinary type")} />
            <input type="text" placeholder={t("employeeForm.subject", "Subject")} value={caseForm.subject} onChange={(e) => setCaseForm({ ...caseForm, subject: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
            <textarea placeholder={t("employeeForm.description", "Description")} value={caseForm.description} onChange={(e) => setCaseForm({ ...caseForm, description: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" rows={2} />
            <div className="flex gap-2">
              <button type="button" onClick={() => void create()} disabled={saving} className="rounded-lg bg-pme-green px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">{t("common.save", "Save")}</button>
              <button type="button" onClick={() => setAdding(false)} className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700">{t("common.cancel", "Cancel")}</button>
            </div>
          </div>
        )}
        {cases.length > 0 ? (
          <div className="space-y-2">
            {cases.map((c) => (
              <div key={c.id} className="rounded-lg border border-gray-200 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{c.subject}</span>
                    <span className="ml-2 text-xs text-gray-400">{c.case_reference}</span>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusColor(c.status)}`}>
                    {c.status.replace(/_/g, " ")}
                  </span>
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {c.disciplinary_type} &middot; {t("employeeForm.opened", "Opened")} {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}
                  {c.outcome && <> &middot; {t("employeeForm.outcome", "Outcome")}: {c.outcome.replace(/_/g, " ")}</>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400">{t("employeeForm.noDisciplinaryCases", "No disciplinary cases.")}</p>
        )}
      </div>
    </Section>
  );
}

// --- Assets -----------------------------------------------------------------


function AssetsSection({ employeeId }: { employeeId: number }) {
  const [items, setItems] = useState<EmployeeAsset[]>([]);
  const [editing, setEditing] = useState<EmployeeAsset | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await api.get<EmployeeAsset[]>(
        `/api/employees/${employeeId}/assets`,
      );
      setItems(list);
    } catch {
      setItems([]);
    }
  }, [employeeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (id: number) => {
    if (!confirm("Delete this asset record?")) return;
    await api.delete(`/api/assets/${id}`);
    await load();
  };

  const formNode = (
    <AssetForm
      employeeId={employeeId}
      initial={editing ?? undefined}
      onSaved={async () => {
        setAdding(false);
        setEditing(null);
        await load();
      }}
      onCancel={() => {
        setAdding(false);
        setEditing(null);
      }}
    />
  );

  return (
    <EntitySection
      title="Assets"
      adding={adding || editing !== null}
      onAdd={() => {
        setAdding(true);
        setEditing(null);
      }}
      onCancel={() => {
        setAdding(false);
        setEditing(null);
      }}
      form={formNode}
    >
      {items.length === 0 ? (
        <p className="text-xs text-gray-400">No assets recorded.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((a) => (
            <li key={a.id}>
              <EntityRow
                primary={
                  <>
                    {a.asset_description}{" "}
                    <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700">
                      {a.asset_category}
                    </span>
                  </>
                }
                secondary={
                  [a.serial_number, a.condition_on_loan]
                    .filter(Boolean)
                    .join(" · ") || undefined
                }
                meta={
                  <>
                    {a.date_loaned
                      ? `Loaned ${fmtDate(a.date_loaned)}`
                      : ""}
                    {a.date_returned
                      ? ` · Returned ${fmtDate(a.date_returned)}`
                      : ""}
                  </>
                }
                onEdit={() => {
                  setEditing(a);
                  setAdding(false);
                }}
                onDelete={() => void remove(a.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </EntitySection>
  );
}

function AssetForm({
  employeeId,
  initial,
  onSaved,
  onCancel,
}: {
  employeeId: number;
  initial?: EmployeeAsset;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const [assetCategory, setAssetCategory] = useState(initial?.asset_category ?? "Computer");
  const [assetDescription, setAssetDescription] = useState(initial?.asset_description ?? "");
  const [serialNumber, setSerialNumber] = useState(initial?.serial_number ?? "");
  const [dateLoaned, setDateLoaned] = useState(initial?.date_loaned ?? "");
  const [dateReturned, setDateReturned] = useState(initial?.date_returned ?? "");
  const [conditionOnLoan, setConditionOnLoan] = useState(initial?.condition_on_loan ?? "");
  const [conditionOnReturn, setConditionOnReturn] = useState(initial?.condition_on_return ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!assetDescription.trim() || !assetCategory.trim()) return;
    setSaving(true);
    try {
      const body = {
        asset_category: assetCategory,
        asset_description: assetDescription.trim(),
        serial_number: serialNumber || null,
        date_loaned: dateLoaned || null,
        date_returned: dateReturned || null,
        condition_on_loan: conditionOnLoan || null,
        condition_on_return: conditionOnReturn || null,
        notes: notes || null,
      };
      if (initial) {
        await api.put(`/api/assets/${initial.id}`, body);
      } else {
        await api.post(`/api/employees/${employeeId}/assets`, body);
      }
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/40 p-3">
      <Row>
        <Field label="Category">
          <DropdownSelect
            listCode="asset_category"
            value={assetCategory}
            onChange={setAssetCategory}
          />
        </Field>
        <Field label="Description">
          <input
            value={assetDescription}
            onChange={(e) => setAssetDescription(e.target.value)}
            className="input"
            required
          />
        </Field>
      </Row>
      <Field label="Serial number">
        <input
          value={serialNumber}
          onChange={(e) => setSerialNumber(e.target.value)}
          className="input"
        />
      </Field>
      <Row>
        <Field label="Date loaned">
          <input
            type="date"
            value={dateLoaned}
            onChange={(e) => setDateLoaned(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Date returned">
          <input
            type="date"
            value={dateReturned}
            onChange={(e) => setDateReturned(e.target.value)}
            className="input"
          />
        </Field>
      </Row>
      <Row>
        <Field label="Condition on loan">
          <input
            value={conditionOnLoan}
            onChange={(e) => setConditionOnLoan(e.target.value)}
            className="input"
            placeholder="e.g. New, Good"
          />
        </Field>
        <Field label="Condition on return">
          <input
            value={conditionOnReturn}
            onChange={(e) => setConditionOnReturn(e.target.value)}
            className="input"
            placeholder="e.g. Good, Damaged"
          />
        </Field>
      </Row>
      <Field label="Notes">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="input"
        />
      </Field>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || !assetDescription.trim()}
          className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        >
          {saving ? "Saving..." : initial ? "Update" : "Add"}
        </button>
      </div>
    </div>
  );
}

// --- Equipment Requisitions -------------------------------------------------

const REQUISITION_STATUS_BADGE: Record<RequisitionStatus, string> = {
  pending: "bg-amber-50 text-amber-700 ring-amber-200",
  approved: "bg-green-50 text-green-700 ring-green-200",
  rejected: "bg-red-50 text-red-700 ring-red-200",
  ordered: "bg-blue-50 text-blue-700 ring-blue-200",
  delivered: "bg-gray-100 text-gray-600 ring-gray-200",
  cancelled: "bg-gray-100 text-gray-500 ring-gray-200",
};

function RequisitionsSection({ employeeId }: { employeeId: number }) {
  const [items, setItems] = useState<EquipmentRequisition[]>([]);
  const [editing, setEditing] = useState<EquipmentRequisition | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await api.get<EquipmentRequisition[]>(
        `/api/employees/${employeeId}/requisitions`,
      );
      setItems(list);
    } catch {
      setItems([]);
    }
  }, [employeeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (id: number) => {
    if (!confirm("Delete this requisition?")) return;
    await api.delete(`/api/requisitions/${id}`);
    await load();
  };

  const formNode = (
    <RequisitionForm
      employeeId={employeeId}
      initial={editing ?? undefined}
      onSaved={async () => {
        setAdding(false);
        setEditing(null);
        await load();
      }}
      onCancel={() => {
        setAdding(false);
        setEditing(null);
      }}
    />
  );

  return (
    <EntitySection
      title="Equipment Requisitions"
      adding={adding || editing !== null}
      onAdd={() => {
        setAdding(true);
        setEditing(null);
      }}
      onCancel={() => {
        setAdding(false);
        setEditing(null);
      }}
      form={formNode}
    >
      {items.length === 0 ? (
        <p className="text-xs text-gray-400">No requisitions.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((r) => (
            <li key={r.id}>
              <div className="flex items-start justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {r.item_description}
                    <span
                      className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ${REQUISITION_STATUS_BADGE[r.status]}`}
                    >
                      {r.status}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs text-gray-600">
                    Qty {r.quantity}
                    {r.estimated_budget != null
                      ? ` · Est ${r.estimated_budget}`
                      : ""}
                  </p>
                  {r.reason && (
                    <p className="mt-0.5 text-xs text-gray-500">
                      {r.reason}
                    </p>
                  )}
                  {r.date_requested && (
                    <p className="mt-0.5 text-[11px] text-gray-400">
                      Requested {fmtDate(r.date_requested)}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(r);
                      setAdding(false);
                    }}
                    className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => void remove(r.id)}
                    className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </EntitySection>
  );
}

function RequisitionForm({
  employeeId,
  initial,
  onSaved,
  onCancel,
}: {
  employeeId: number;
  initial?: EquipmentRequisition;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const [itemDescription, setItemDescription] = useState(
    initial?.item_description ?? "",
  );
  const [quantity, setQuantity] = useState<string>(
    String(initial?.quantity ?? 1),
  );
  const [estimatedBudget, setEstimatedBudget] = useState<string>(
    initial?.estimated_budget != null ? String(initial.estimated_budget) : "",
  );
  const [reason, setReason] = useState(
    initial?.reason ?? "",
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!itemDescription.trim()) return;
    setSaving(true);
    try {
      const qty = parseInt(quantity, 10);
      const cost = estimatedBudget ? parseFloat(estimatedBudget) : null;
      const body = {
        item_description: itemDescription.trim(),
        quantity: isNaN(qty) || qty < 1 ? 1 : qty,
        estimated_budget: cost,
        reason: reason || null,
      };
      if (initial) {
        await api.put(`/api/requisitions/${initial.id}`, body);
      } else {
        await api.post(`/api/employees/${employeeId}/requisitions`, body);
      }
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/40 p-3">
      <Field label="Item description">
        <input
          value={itemDescription}
          onChange={(e) => setItemDescription(e.target.value)}
          className="input"
          required
        />
      </Field>
      <Row>
        <Field label="Quantity">
          <input
            type="number"
            min={1}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="input max-w-[120px]"
          />
        </Field>
        <Field label="Estimated budget">
          <input
            type="number"
            step="0.01"
            value={estimatedBudget}
            onChange={(e) => setEstimatedBudget(e.target.value)}
            className="input"
          />
        </Field>
      </Row>
      <Field label="Reason">
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className="input"
        />
      </Field>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || !itemDescription.trim()}
          className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        >
          {saving ? "Saving..." : initial ? "Update" : "Add"}
        </button>
      </div>
    </div>
  );
}
